/* eslint-disable max-len */
/* eslint-disable no-param-reassign */
/* eslint-disable no-console */
import * as am4core from '@amcharts/amcharts4/core';
import { CircularProgress, makeStyles, Paper, Theme } from '@material-ui/core';
import { AppAPI, EventLogData, EventLogRecord, ModeAppProxyAPI } from '@moderepo/mode-apis';
import { getLanguageByCode, capitalizeAll, Time, CompPanelHeader, useModePanelStyle, CompPanelHeaderProps, BaseCompSubAction, BaseCompAction,
    GenericMessageBox, TimeSeriesDataTable, TimeSeriesDataTableProps, useSearchParams, MultiMetricsDataPoint, ITEMS_PER_PAGE_100,
    PaginationBar, ITEMS_PER_PAGE_LIST, PAGING_MIN_PAGES, TimeSeriesTableCompField } from '@moderepo/mode-app-components';
import { ritzDataContext, selectLoggedInAgent } from '@moderepo/mode-data-state-ritz';
import { Agent, SortOrder, TimeSeriesAggregation, TimeSeriesCollectionData, TimeSeriesCollectionRawData, TimeSeriesResolution }
    from '@moderepo/mode-internal-apis';
import { modeUIContext, selectLanguage, showSlideOutPanel, SlideOutPanelLocation } from '@moderepo/mode-ui-state';
import React, { CSSProperties, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import * as am4charts from '@amcharts/amcharts4/charts';
import clsx from 'clsx';
import { ChartMetricInfo, ExtChartSettings, GanttChartDataPoint, SynchronizedNumericTagMetricsDisplayContainer,
    SynchronizedNumericTagMetricsDisplayContainerProps, ChartViewSettings, MultiDataSourcesExportDataCompOptions, MultiDataSourcesSlideOutPanelType,
    ExportDataFormat, MultiDataSourcesViewOptions, getLongestMetricNameInPixel, MetricViewSettings } from '.';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from '../../utils/constants';
import { useRitzTranslation } from '../../utils/customHooks';
import { useSelectProject } from '../../utils/dataStateHooks';
import { getPreferredLanguage, ReadonlyNonEmptyArray } from '../../utils/utils';
import { transformMultiMetricsData } from './tsUtils';
import { TableViewSettings } from '..';


const useStyle = makeStyles((theme: Theme) => {
    return {
        viewPanel: {
            '&:last-child:not(:first-child)': {
                marginTop: theme.spacing(1),
            },
        },

        panelHeader: {
        },

        panelContent: {
            padding      : 0,
            height       : '100%',
            display      : 'flex',
            flexDirection: 'column',
        },

        legendsContainer: {
            padding       : theme.spacing(2),
            width         : '100%',
            display       : 'flex',
            flexDirection : 'row',
            alignItems    : 'center',
            justifyContent: 'flex-start',
            flexWrap      : 'wrap',
            alignSelf     : 'center',
        },

        legendLabel: {
            width         : '10em',
            margin        : theme.spacing(0.5, 2),
            display       : 'flex',
            flexDirection : 'row',
            alignItems    : 'center',
            justifyContent: 'flex-start',
            cursor        : 'pointer',
            whiteSpace    : 'normal',
            wordBreak     : 'break-all',
            '& .label'    : {
                color: theme.palette.text.primary,
            },

            '&.numeric': {
                '&:before': {
                    content    : '""',
                    display    : 'inline-block',
                    minWidth   : 15,
                    height     : 15,
                    background : 'currentColor',
                    marginRight: 5,
                    border     : `1px solid ${theme.palette.text.primary}`,
                },
            },
            '&.tag': {
                '&:before': {
                    content    : '""',
                    display    : 'inline-block',
                    minWidth   : 15,
                    height     : 15,
                    background : 'currentColor',
                    marginRight: 5,
                    border     : `1px solid ${theme.palette.text.primary}`,
                },
            },
        },

        chart: {
            flex      : 1,
            height    : '100%',
            background: theme.palette.background.paper,
        },

        noDataMessage: {
            display       : 'flex',
            flexDirection : 'column',
            alignItems    : 'center',
            justifyContent: 'center',
            flex          : 1,
            height        : '100%',
        },

        [theme.breakpoints.up('md')]: {
            viewPanel: {
                '&:last-child:not(:first-child)': {
                    marginTop: theme.spacing(2),
                },
            },
        },

        [theme.breakpoints.up('lg')]: {
            viewPanel: {
                '&:last-child:not(:first-child)': {
                    marginTop: theme.spacing(3),
                },
            },
        },
    };
}, { name: 'MultiDataSourcesViewContainer', index: 1 });



/**
 * The params required for fetching data for a given time range
 */
export interface FetchDataByTimeRangeOptions {
    readonly startTime: number;
    readonly endTime: number;
}


/**
 * Type guard function to check if the provided option is a FetchDataByTimeRangeOptions
 */
const isFetchDataByTimeRangeOptions = (obj: unknown): obj is FetchDataByTimeRangeOptions => {
    const options = obj as FetchDataByTimeRangeOptions;
    return options !== undefined && typeof options === 'object'
        && typeof options.startTime === 'number'
        && typeof options.endTime === 'number';
};



/**
 * The params required for fetching aggregated collection data which extends FetchDataByTimeRangeOptions
 */
export interface FetchAggregatedDataOptions extends FetchDataByTimeRangeOptions {
    readonly aggregation?: TimeSeriesAggregation | undefined;
    readonly resolution?: TimeSeriesResolution | undefined;
}


/**
 * Type guard function to check if the provided option is a FetchAggregatedDataOptions
 */
const isFetchAggregatedDataOptions = (obj: unknown): obj is FetchAggregatedDataOptions => {
    const options = obj as FetchAggregatedDataOptions;
    return options !== undefined && typeof options === 'object'
        && (options.aggregation === undefined || Object.values(TimeSeriesAggregation).includes(options.aggregation))
        && (options.resolution === undefined || Object.values(TimeSeriesResolution).includes(options.resolution))
        && isFetchDataByTimeRangeOptions(options);
};


/**
 * The params required for fetching raw collection data
 */
export interface FetchRawDataOptions extends FetchDataByTimeRangeOptions {
    // The number of data points to fetch. If the value is a POSITIVE number, the component will fetch data starting from the startTime.
    // If the value is NEGATIVE, the component will fetch data starting from the endTime.
    readonly limit: number;
}


/**
 * Type guard function to check if the provided option is a FetchRawDataOptions
 */
const isFetchRawDataOptions = (obj: unknown): obj is FetchRawDataOptions => {
    const options = obj as FetchRawDataOptions;
    return options !== undefined && typeof options === 'object'
        && typeof options.limit === 'number'
        && isFetchDataByTimeRangeOptions(options);
};


export interface FetchDataNumericMetricParams extends ChartMetricInfo {
    readonly transform?: {
        readonly formula: string;
        readonly displayNameKey?: string | undefined;
        readonly unitKey?: string | undefined;
    } | undefined;
}


export interface FetchDataTagMetricParams extends ChartMetricInfo {
}


export interface FetchDataParams {
    // The optional list of numeric metrics we want to fetch
    readonly numericMetrics?: readonly FetchDataNumericMetricParams[] | undefined;

    // The optional list of tag metrics we want to fetch
    readonly tagMetrics?: readonly FetchDataTagMetricParams[] | undefined;

    // This component displays data from multiple collections and it is possible that the collections have the same numeric/tag metric
    // display name. Therefore, we might need to have a display name prefix for each collection so that we can combine it with the numeric/tag
    // metrics display name to make them unique.
    readonly metricDisplayNamePrefix: string;
}


/**
 * The interface for all the params required for fetching data for 1 collection. To fetch data for a collection, we will need these
 * params.
 */
export interface FetchCollectionDataParams extends FetchDataParams {
    readonly homeId: number;
    readonly smartModuleId: string;
    readonly collectionId: string;
    readonly metricsDefinitionId: string;
}


/**
 * The interface for all the params required for fetching data for 1 event log. To fetch data for a collection, we will need these
 * params.
 */
export interface FetchEventLogDataParams extends FetchDataParams {
    readonly homeId: number;
    readonly logSourcePath: string;
    readonly searchKeys: string;

    // Event logs data are project specific. Each project has different fields for the events. To be able to use the event log data, the
    // parent container MUST provide these settings in order for this component to know how to use the data.
    readonly eventLogDataViewSettings: {
        // Events are grouped by some ID. An event log contains many fields and some of them are used for composing unique event group ID. This
        // array specifies which fields are needed to compose unique event group id. For example, if a group ID is consisted of fields
        // ['block', 'holeNo', 'stage'], then all events that have the same values for these 3 fields will belonged to the same group.
        readonly groupIdCompositionFields: readonly string[];

        // The field that contains the value for the event name. Since the log record is project specific, frontend doesn't know which
        // field from the record contain the value for the event. This prop tells the frontend which field to look at.
        readonly eventNameField: string;

        // All log events will have start/end events. We will not assume the value for these event will be 'start' and 'end' since
        // different project can use different value e.g. 'begin' and 'end', 'start' and 'stop', etc... So use these 2 props to tell the
        // frontend which value will be used as start event and which value will be used as stop event.
        readonly startEventValue: string;
        readonly endEventValue: string;

        // The field that contain the value for the event
        readonly eventValueField: string;

        // The map of eventValue => color string. This is needed to color the bar. If this is not specified, a default color is used and
        // all the bar will have the same colors which might not be easy to look at
        readonly colorsByEventValue?: {
            readonly [eventValue: string]: string | undefined;
        } | undefined
    };
}



/**
 * Merge data from the new collection into the "globalNumericChartData". We will group all NUMERIC metric values that have the same timestamp into the
 * same dataPoint object.
 *
 * Given the new collection data in this format
 *      {
 *          collectionId: "...",
 *          begin: "...",
 *          end: "...",
 *          data: [
 *              ["2021-11-01T12:00:00:00:00", 56, 2, 6, "tagValue1_1", "tagValue2_1"],
 *              ["2021-11-01T13:00:00:00:00", 34, 6, 23, "tagValue1_2", "tagValue2_2"],
 *              ["2021-11-01T14:00:00:00:00", 5, 5, 34, "tagValue1_3", "tagValue2_3"],
 *              ...
 *          ]
 *      }
 *
 * And "globalNumericChartData" which already contained data from other collection
 *      {
 *          dataPoints: [{
 *              date: 1635735600000,
 *              existing_numeric_metric_1: 5454,
 *              existing_numeric_metric_3: 2345,
 *              existing_tag_metric_1: "existing tagValue 1",
 *          }, {
 *              date: 1635739200000,
 *              existing_numeric_metric_1: 54,
 *              existing_numeric_metric_3: 45,
 *              existing_tag_metric_1: "existing tagValue 1",
 *          }, {
 *              date: 1635742800000,
 *              existing_numeric_metric_1: 43,
 *              existing_numeric_metric_3: 36,
 *              existing_tag_metric_1: "existing tagValue 1",
 *          },
 *          ...
 *          ]
 *      }
 *
 * The result will be this
 *      {
 *          dataPoints: [{
 *              date: 1635735600000,
 *              existing_numeric_metric_1: 5454,
 *              existing_numeric_metric_3: 2345,
 *              existing_tag_metric_1: "existing tagValue 1",
 *
 *              // Data from the new collection
 *              numeric_metric_1: 56,
 *              numeric_metric_2: 2,
 *              numeric_metric_3: 6,
 *              tag_metric_1: "tagValue1_1",
 *              tag_metric_2: "tagValue2_1",
 *          }, {
 *              date: 1635739200000,
 *              existing_numeric_metric_1: 54,
 *              existing_numeric_metric_3: 45,
 *              existing_tag_metric_1: "existing tagValue 1",
 *
 *              // Data from the new collection
 *              numeric_metric_1: 34,
 *              numeric_metric_2: 6,
 *              numeric_metric_3: 23,
 *              tag_metric_1: "tagValue1_2",
 *              tag_metric_2: "tagValue2_2",
 *          }, {
 *              date: 1635742800000,
 *              existing_numeric_metric_1: 43,
 *              existing_numeric_metric_3: 36,
 *              existing_tag_metric_1: "existing tagValue 1",
 *
 *              // Data from the new collection
 *              numeric_metric_1: 5,
 *              numeric_metric_2: 5,
 *              numeric_metric_3: 34,
 *              tag_metric_1: "tagValue1_3",
 *              tag_metric_2: "tagValue2_3",
 *          },
 *          ...
 *          ]
 *      }
 *
 * NOTE: the "timestampToDataPointMap" is just map that contains pointers to the dataPoints' elements. This allow us to quickly look up existing
 * data points based on timestamp. We don't need to loop through the dataPoints array every time.
 */
const mergeCollectionDataToGlobalChartData = (
    collectionData: TimeSeriesCollectionData | TimeSeriesCollectionRawData,
    collectionInfo: FetchCollectionDataParams,
    globalNumericChartData: {
        dataPoints: MultiMetricsDataPoint[];
        timestampToDataPointMap: { [timestamp: string]: MultiMetricsDataPoint | undefined; };
    },
    startTime: number,
    endTime: number,
): typeof globalNumericChartData => {

    collectionData.data.forEach((dataRow) => {

        // Get the timestamp value from the data row's 1st column
        const timestamp = dataRow[0];
        const timestampValue = Date.parse(timestamp);

        // If the dataRow's timestamp is out of range then ignore this data. This can happen because when we fetch RAW data, we can't specify the
        // time range. So it is possible for the back end to return more data than we need.
        if (timestampValue < startTime || timestampValue > endTime) {
            return;
        }

        // Get the dataPoint object for the given timestamp. This will be either an existing dataPoint that has the same timestamp
        // or a new dataPoint object
        const dataPoint = (() => {

            // Find a data point from the timestampToDataPointMap for the given 'timestamp'.
            const existingDataPoint = globalNumericChartData.timestampToDataPointMap[timestamp];

            // We found a data point object in timestampToDataPointMap that has the same timestamp, then return that dataPoint object
            if (existingDataPoint !== undefined) {
                return existingDataPoint;
            }

            // If not, create a new dataPoint object and add it to the dataPoints array AND also add it to the timestampToDataPointMap so that
            // if we find another data point later that has the same timestamp, can use the same object
            const newDataPoint: MultiMetricsDataPoint = {
                dataPointTimestamp: timestampValue,
            };

            // eslint-disable-next-line no-param-reassign
            globalNumericChartData.timestampToDataPointMap[timestamp] = newDataPoint;
            globalNumericChartData.dataPoints.push(newDataPoint);
            return newDataPoint;
        })();


        // Get the NUMERIC values from the collection's dataRow and add them to the dataPoint
        collectionInfo.numericMetrics?.forEach((metric, index) => {
            // Backend return values for numeric metrics first and they start from index 1 because value at index is for the timestamp
            const metricValue = dataRow[index + 1];

            // Add the metric's value to the data point using the metric's id as key
            dataPoint[metric.id] = metricValue;
        });

        // Get the TAG values from the collection's dataRow and add them to the dataPoint
        // Backend return values for tag metrics AFTER numeric metrics therefore the startIndex where tag metric value will start from is
        // the number of numeric metrics + 1 for the Date column
        const startIndex = (collectionInfo.numericMetrics?.length ?? 0) + 1;
        collectionInfo.tagMetrics?.forEach((metric, index) => {
            const metricValue = dataRow[startIndex + index];

            // Add the metric's value to the data point using the metric's id as key
            dataPoint[metric.id] = metricValue;
        });
    });

    return globalNumericChartData;
};


/**
 * Pick a color for the tag metric bar. First, try to get the color from the metric.enumDefinitions. If there is a color defined
 * specifically for the 'tagValue' then use it. If not, use the color from metric.color. If we use metric.color, we need to
 * give each value a different shade so that they don't all look the same.
 */
const getTagMetricValueColor = (
    tagValue: string | number | null,
    metricId: string,
    metricColor: string | undefined,
    metricEnumDefinition: readonly {
        value: string;
        color?: string | undefined;
    }[] | undefined,
    tagValueColorGroupedByMetricId: {
        [metricId: string]: {
            [tagValue: string]: string | undefined;
        } | undefined;
    },
): string | undefined => {
    if (tagValue === null || tagValue === undefined) {
        return undefined;
    }

    if (metricEnumDefinition) {
        // Find the enumDefinition that is defined for the specified tag value
        const matchEnumDef = metricEnumDefinition.find((def) => {
            return def.value === tagValue;
        });
        if (matchEnumDef?.color) {
            return matchEnumDef.color;
        }
    }

    // Can't find color set in enumDefinition, use the metric color but make sure to give each value a different shade if they are
    // different value. If the value are the same, use the same shade. This means when we choose a shade for a tagValue, we need
    // to remember it so that we can use the same shade for the same value
    if (metricColor) {
        const colorsByMetricId = tagValueColorGroupedByMetricId[metricId];
        if (colorsByMetricId && colorsByMetricId[tagValue]) {
            return colorsByMetricId[tagValue];
        } if (colorsByMetricId && !colorsByMetricId[tagValue]) {
            // We have already created a map of colors for the metric but this map does not have a color for the tagValue yet.
            // We need to add a new shade of metric.color to this map. We will make the new color darker than the metric.color.
            // by 10% for each tagValue. For example, the first tagValue will use metric.color. The second will be 10% darker
            // then metric.color. The third will be 20% darker. The forth will be 30% darker, etc...
            const count = Object.values(colorsByMetricId).length;
            const newColorShade = am4core.color(metricColor).brighten(count * -0.1).hex;
            colorsByMetricId[tagValue] = newColorShade;
            return newColorShade;
        }

        // This is the first value we encountered for the given metric therefore we don't have a map of colors for the metric yet.
        // So we just need to create a new map for the metric and initialize it with metric.color as the first color
        if (!colorsByMetricId) {
            // eslint-disable-next-line no-param-reassign
            tagValueColorGroupedByMetricId[metricId] = {
                [tagValue]: metricColor,
            };
            return metricColor;
        }
        return metricColor;
    }

    return undefined;
};


/**
 * Convert TAG metric data from a collection to an array of GanttChartDataPoint. These are the data object that the chart can work with.
 *
 * Given a collection data
 *      {
 *          collectionId: "...",
 *          begin: "...",
 *          end: "...",
 *          data: [
 *              ["2021-11-01T12:00:00:00:00", 56, 2, 6, "tagValue1", "tagValue2"],
 *              ["2021-11-01T13:00:00:00:00", 34, 6, 23, "tagValue1", null],
 *              ["2021-11-01T14:00:00:00:00", 5, 5, 34, "tagValue1.1", "tagValue2.1"],
 *              ["2021-11-01T15:00:00:00:00", 5, 5, 34, "tagValue1.1", "tagValue2.1"],
 *              ...
 *          ]
 *      }
 *
 * This will return
 *      [{
 *          metricId: "tag_metric_1",
 *          startDate: "2021-11-01T12:00:00:00:00",
 *          endDate: "2021-11-01T14:00:00:00:00",
 *          value: "tagValue1"
 *      }, {
 *          metricId: "tag_metric_1",
 *          startDate: "2021-11-01T14:00:00:00:00",
 *          endDate: "2021-11-01T14:00:00:00:00",
 *          value: "tagValue1.1"
 *      }, {
 *          metricId: "tag_metric_2",
 *          startDate: "2021-11-01T12:00:00:00:00",
 *          endDate: "2021-11-01T13:00:00:00:00",
 *          value: "tagValue2"
 *      }, {
 *          metricId: "tag_metric_2",
 *          startDate: "2021-11-01T14:00:00:00:00",
 *          endDate: "2021-11-01T14:00:00:00:00",
 *          value: "tagValue2.1"
 *      }]
 *
 * Basically, these 4 data points represent 4 bars/blocks on the chart like this
 *      Time            12              13              14              15
 *      tag_metric_1    |-----------tagValue1------------|--tagValue1.1--|
 *      tag_metric_2    |---tagValue2---|                |--tagValue2.1--|
 *
 */
const mergeCollectionTagDataToGlobalGanttChartData = (
    collectionData: TimeSeriesCollectionData | TimeSeriesCollectionRawData,
    collectionInfo: FetchCollectionDataParams,
    globalGanttChartData: {
        [metricId: string]: GanttChartDataPoint[];
    },
): typeof globalGanttChartData => {

    // First, sort the data rows from the collectionData by 'date'. Because if we fetch the collection data by -limit, the response data from
    // backend will be in reverse order.
    const dataRows = [
        ...collectionData.data,
    ].sort((row1, row2) => {
        const date1 = row1[0];
        const date2 = row2[0];
        return Date.parse(date1) - Date.parse(date2);
    });


    /**
     * Next, go through all the dataRows, create 1 data point for each tag value, and group all tag values of the same metrics into the
     * same group. The result will be this
     *
     * tagValueColorGroupedByMetricId = {
     *      tagMetric1: [
     *          {startDate: "2021-11-01T12:00:00:00:00", endDate: "2021-11-01T12:00:00:00:00", value: "tagValue1", color: "..."},
     *          {startDate: "2021-11-01T13:00:00:00:00", endDate: "2021-11-01T13:00:00:00:00", value: "tagValue1", color: "..."},
     *          {startDate: "2021-11-01T14:00:00:00:00", endDate: "2021-11-01T14:00:00:00:00", value: "tagValue1.1", color: "..."},
     *          {startDate: "2021-11-01T15:00:00:00:00", endDate: "2021-11-01T15:00:00:00:00", value: "tagValue1.1", color: "..."},
     *      ],
     *      tagMetric2: [
     *          {startDate: "2021-11-01T12:00:00:00:00", endDate: "2021-11-01T12:00:00:00:00", value: "tagValue2", color: "..."},
     *          {startDate: "2021-11-01T13:00:00:00:00", endDate: "2021-11-01T13:00:00:00:00", value: null, color: "..."},
     *          {startDate: "2021-11-01T14:00:00:00:00", endDate: "2021-11-01T14:00:00:00:00", value: "tagValue2.1", color: "..."},
     *          {startDate: "2021-11-01T15:00:00:00:00", endDate: "2021-11-01T15:00:00:00:00", value: "tagValue2.1", color: "..."},
     *      ]
     * }
     */


    /**
     * A map of color keyed by tag value and grouped by metricId.
     *      {
     *          status: {
     *              error: "#ff0000", warn: "#ffff00", info: "#0000ff"
     *          },
     *          state: {
     *              boring: "#ff0000", pressure: "#ffff00", inject: "#0000ff"
     *          }
     *      }
     */
    const tagValueColorGroupedByMetricId: {
        [metricId: string]: {
            [tagValue: string]: string | undefined;
        }| undefined;
    } = {};


    dataRows.forEach((dataRow) => {

        // Get the timestamp value from the data row's 1st column
        const timestamp = Date.parse(dataRow[0]);

        // Get the TAG values from the collection's dataRow and add them to the dataPoint
        const numericMetricsCount = collectionInfo.numericMetrics ? collectionInfo.numericMetrics.length : 0;


        collectionInfo.tagMetrics?.forEach((metric, index) => {

            // Tag metric values comes AFTER numeric values therefore the index of tag metrics value is numericMetricsCount + index + 1
            const tagValue = dataRow[numericMetricsCount + index + 1];
            const tagDisplayValue = (() => {
                // If the metric has enumDefinition, see if there is a display name configured for the tagValue. If there is, use it
                if (tagValue && metric.enumDefinitions) {
                    const enumDef = metric.enumDefinitions.find((enumDef) => {
                        return enumDef.value === tagValue;
                    });
                    if (enumDef) {
                        return enumDef.name;
                    }
                }
                return tagValue;
            })();

            // Add the metric's value to the data point using the metric's id as key

            // Get the array of dataPoints for the metric.id from the 'dataArrayGroupedByMetricId'. If it does not exist, create a new []
            const dataArray = globalGanttChartData[metric.id] ?? [];

            if (!globalGanttChartData[metric.id]) {
                // If we created a new dataArray then add the new dataArray to the 'dataArrayGroupedByMetricId'
                globalGanttChartData[metric.id] = dataArray;
            }

            const color = getTagMetricValueColor(tagValue, metric.id, metric.color, metric.enumDefinitions, tagValueColorGroupedByMetricId);

            // Push a new dataPoint to the dataArray
            dataArray.push({
                metricId   : metric.id,
                displayName: metric.displayName,
                startDate  : timestamp,
                endDate    : timestamp,
                value      : tagDisplayValue?.toString(),
                color,
            });
        });
    });


    /**
     * Then merge all data points that have the same value into blocks. If we see 2 data points that have the same value, then we
     * can combine them using the first dataPoint's startDate and the second dataPoint's endDate. For example these 2 data points
     *
     * Before:
     *      {startDate: "2021-11-01T12:00:00:00:00", endDate: "2021-11-01T12:00:00:00:00", value: "tagValue1", color: "..."}
     *      {startDate: "2021-11-01T13:00:00:00:00", endDate: "2021-11-01T13:00:00:00:00", value: "tagValue1", color: "..."}
     *
     * After:
     *      {startDate: "2021-11-01T12:00:00:00:00", endDate: "2021-11-01T13:00:00:00:00", value: "tagValue1", color: "..."}
     */
    Object.entries(globalGanttChartData).forEach(([metricId, dataPointsArray]) => {
        const mergedDataArray: typeof dataPointsArray = [];

        // Pointer to the current block we are processing. currentBlock is also a tag data point
        let currentBlock: GanttChartDataPoint | undefined;

        dataPointsArray.forEach((currentDataPoint) => {
            if (!currentBlock) {
                // If we are not processing any block AND if currentDataPoint is not null/undefined then just push it to the 'mergedDataArray'
                // to start a new block
                if (currentDataPoint.value !== null && currentDataPoint.value !== undefined) {
                    mergedDataArray.push(currentDataPoint);
                    currentBlock = currentDataPoint;
                }
            } else if (currentBlock.value === currentDataPoint.value) {
                // if the currentDataPoint has the same value as the currentBlock then merge the currentDataPoint with the currentBlock.
                // This basically throw away the 'currentDataPoint' and extends the currentBlock.endDateValue using the
                // currentDataPoint.startDateValue
                currentBlock.endDate = currentDataPoint.startDate;
            } else {
                // If the currentDataPoint has a different value then the currentBlock then we need to end the currentBlock and start a new BLOCK

                // First, we will set the endDataValue for the currentBlock as the currentDataPoint.startDateValue
                currentBlock.endDate = currentDataPoint.startDate;

                // Then we push the currentDataPoint to the mergedDataArray to create a new block but only if its value is not null/undefined
                if (currentDataPoint.value !== null && currentDataPoint.value !== undefined) {
                    mergedDataArray.push(currentDataPoint);
                    currentBlock = currentDataPoint;
                } else {
                    // The currentDataPoint.value is null/undefined which mean the current block stop here. We will start a new block when we see
                    // another non-null data point.
                    currentBlock = undefined;
                }
            }
        });

        globalGanttChartData[metricId] = mergedDataArray;
    });

    return globalGanttChartData;
};



/**
 * Helper function that take an array of event records and group them by groupId.
 */
export const groupEventLogRecordsByGroupId = (
    eventLogData: EventLogData,
    eventLogInfo: FetchEventLogDataParams,
): {
    [groupId: string]: EventLogRecord[];
} => {

    /**
     * First, group the events by their unique groupId. groupId is a unique string composed by using the values from 1 or more fields in the record.
     * For example: let groupIdCompositionFields = ["block", "holeNo"] and these are the records
     *      [
     *          {fields: {block: "A", holeNo: 1, progress: "test1", ...}},
     *          {fields: {block: "A", holeNo: 1, progress: "test2", ...}},
     *          {fields: {block: "A", holeNo: 5, progress: "test3", ...}},
     *          {fields: {block: "A", holeNo: 5, progress: "test4", ...}},
     *          {fields: {block: "A", holeNo: 5, progress: "test5", ...}},
     *          {fields: {block: "B", holeNo: 1, progress: "test1", ...}},
     *          {fields: {block: "B", holeNo: 1, progress: "test2", ...}},
     *      ]
     *
     * The result will be
     *      eventRecordsGroupedByGroupId: {
     *          "A:1": [
     *              {fields: {block: "A", holeNo: 1, progress: "test1", ...}},
     *              {fields: {block: "A", holeNo: 1, progress: "test2", ...}},
     *          ],
     *          "A:5": [
     *              {fields: {block: "A", holeNo: 5, progress: "test3", ...}},
     *              {fields: {block: "A", holeNo: 5, progress: "test4", ...}},
     *              {fields: {block: "A", holeNo: 5, progress: "test5", ...}},
     *          ],
     *          "B:1": [
     *              {fields: {block: "B", holeNo: 1, progress: "test1", ...}},
     *              {fields: {block: "B", holeNo: 1, progress: "test2", ...}},
     *          ]
     *      }
     */
    const eventRecordsGroupedByGroupId: {
        [groupId: string]: EventLogRecord[];
    } = {};


    // Get the settings from the eventLogInfo.eventLogDataViewSettings. These settings tell this function how to use the event record data
    const {
        groupIdCompositionFields,
    } = eventLogInfo.eventLogDataViewSettings;


    (eventLogData.records ?? []).forEach((record) => {
        // Create a groupId composed of the values from the list of groupIdCompositionFields
        const groupId = groupIdCompositionFields.reduce((result, fieldName) => {
            if (!result) {
                return record.fields[fieldName];
            }
            return `${result}:${record.fields[fieldName]}`;
        }, '');

        // Get the eventRecordArray for the groupId from the 'eventRecordsGroupedByGroupId'. If it does not exist, create a new []
        const eventRecordArray = eventRecordsGroupedByGroupId[groupId] ?? [];

        if (!eventRecordsGroupedByGroupId[groupId]) {
            // If eventRecordsGroupedByGroupId[groupId] does not exist then add the new eventRecordArray to the 'eventRecordsGroupedByGroupId'
            eventRecordsGroupedByGroupId[groupId] = eventRecordArray;
        }

        // Add the record to the group
        eventRecordArray.push(record);
    });

    return eventRecordsGroupedByGroupId;
};


/**
 * Convert event log records data to array of GanttChartDataPoint and return the array of data points. Each of these data points will be
 * a bar on the Gantt chart.
 *
 * IMPORTANT NOTE: This function will also dynamically create and return an array of Tag Metrics based on the event log data
 */
const genericConvertEventLogRecordsToTagChartData = (
    eventLogData: EventLogData,
    eventLogInfo: FetchEventLogDataParams,
): {
    [metricId: string]: GanttChartDataPoint[];
} => {

    // We will need these timestamp later for some of the events
    const dataRangeStartTime = Date.parse(eventLogData.begin);
    const dataRangeEndTime = Date.parse(eventLogData.end);

    // First, call the groupEventLogRecordsByGroupId to group the records
    const eventRecordsGroupedByGroupId = groupEventLogRecordsByGroupId(eventLogData, eventLogInfo);

    // Get the settings from the eventLogInfo.eventLogDataViewSettings. These settings tell this function how to use the event record data
    const {
        endEventValue,
        eventNameField,
        eventValueField,
        startEventValue,
        colorsByEventValue,
    } = eventLogInfo.eventLogDataViewSettings;



    /**
     * Now merge each pair of the start/end records into 1 data point array. Each of these pair will be a tag metric data point.
     * NOTE that it is possible that the first element in the event records array will be an 'end' event or the last element in the array
     * is a 'start' event. This can happen if we fetch data right in between an event start/end. For example
     *       start                          end start                           end
     *        |--------- boring -------------|   |----------- injection ---------|
     *                           ⬆️                           ⬆️
     *                           We fetch data from her to here
     *
     * For those cases, we can still create data points. We can use the data range's `startTime` as the event's startTime or use the data range's
     * `endTime` as the event's endTime
     */

    const dataArrayGroupedByGroupId: {
        [groupId: string]: GanttChartDataPoint[];
    } = {};



    Object.entries(eventRecordsGroupedByGroupId).forEach(([groupId, recordsArray]) => {
        if (recordsArray.length > 0) {
            // Get the array of dataPoints for the metric.id from the 'dataArrayGroupedByGroupId'. If it does not exist, create a new []
            const dataPointsArray = dataArrayGroupedByGroupId[groupId] ?? [];

            if (!dataArrayGroupedByGroupId[groupId]) {
                // If we created a new dataPointsArray then add the new dataPointsArray to the 'dataArrayGroupedByGroupId'
                dataArrayGroupedByGroupId[groupId] = dataPointsArray;
            }


            recordsArray.reverse();
            let isFirstRecord = true;
            while (recordsArray.length > 0) {
                const startRecord = recordsArray.pop();
                if (startRecord) {

                    const eventName = startRecord.fields[eventNameField];       // either 'start' or 'end' event
                    const eventValue = startRecord.fields[eventValueField];     // the actual event e.g. 'inject', 'boring', etc...

                    // Find the tag metric associated with the 'eventValueField'. If we find the metric and the metric has enumDefinition,
                    // see if there is a display name configured for the eventValue. If there is, use it
                    const metric = eventLogInfo.tagMetrics?.find((metric) => {
                        return metric.name === eventValueField;
                    });
                    const eventDisplayValue = (() => {
                        if (eventValue && metric && metric.enumDefinitions) {
                            const enumDef = metric.enumDefinitions.find((enumDef) => {
                                return enumDef.value === eventValue;
                            });
                            if (enumDef) {
                                return enumDef.name;
                            }
                        }
                        return eventValue;
                    })();

                    if (eventName === startEventValue) {
                        // we found a record for a start event, now look at the next record. The next record should be an end event record
                        // for the same event value or undefined.
                        // If there is no next record, endRecord === undefined, it is ok. It could just mean the event ends outside of the data range.
                        // But if there is a next record BUT the next record is for a different eventValue then we probably found a bad data. We
                        // expect all events to have start/end records next to each other and nothing else in between.
                        const endRecord = recordsArray.pop();

                        if (endRecord) {
                            // We have the next record, need to make sure the endRecord is for the same event
                            if (endRecord.fields[eventValueField] === eventValue) {
                                dataPointsArray.push({
                                    metricId   : groupId,
                                    displayName: groupId,
                                    startDate  : Date.parse(startRecord.timestamp),
                                    endDate    : Date.parse(endRecord.timestamp),
                                    value      : eventDisplayValue,
                                    color      : colorsByEventValue?.[eventValue],
                                });
                            } else {
                                // eslint-disable-next-line max-len
                                console.log(`Invalid record. Expect 'end ${eventValue}' event but found 'end ${endRecord.fields[eventValueField]}' event`, endRecord);
                            }
                        } else {
                            // no next record, it is ok. We can still create a data point and use the dataRangeEndTime as the event's endTime
                            dataPointsArray.push({
                                metricId   : groupId,
                                displayName: groupId,
                                startDate  : Date.parse(startRecord.timestamp),
                                endDate    : dataRangeEndTime,
                                value      : eventDisplayValue,
                                color      : colorsByEventValue?.[eventValue],
                            });
                        }
                    } else if (eventName === endEventValue) {
                        if (isFirstRecord) {
                            // We expect a start event but we found an end event. It is ok if this is the FIRST record. It just mean the event has
                            // started outside our data range. We can still create a data point using the time range 'startTime' as the event's
                            // startTime
                            dataPointsArray.push({
                                metricId   : groupId,
                                displayName: groupId,
                                startDate  : dataRangeStartTime,
                                endDate    : Date.parse(startRecord.timestamp),
                                value      : eventDisplayValue,
                                color      : colorsByEventValue?.[eventValue],
                            });
                        } else {
                            // If this is NOT the first event but we found an 'end' event that mean we are missing a start event. This must be
                            // invalid data
                            console.log(`Invalid record. Expect 'start ${eventValue}' event but found 'end ${eventValue}' event`, startRecord);
                        }
                    }
                }

                isFirstRecord = false;
            }
        }
    });

    return dataArrayGroupedByGroupId;
};



/**
 * Create and return tag metrics info based on the provided event log records
 */
const genericConvertEventLogRecordsToTagMetrics = (
    eventLogData: EventLogData,
    eventLogInfo: FetchEventLogDataParams,
): readonly ChartMetricInfo[] => {


    // First, call the groupEventLogRecordsByGroupId to group the records
    const eventRecordsGroupedByGroupId = groupEventLogRecordsByGroupId(eventLogData, eventLogInfo);

    /**
     * Create tag metrics based on the number of event groups. 1 tag metric per group.
     */
    const tagMetrics = Object.keys(eventRecordsGroupedByGroupId).map((groupId): ChartMetricInfo => {
        return {
            id         : groupId,
            name       : groupId,
            displayName: groupId,
        };
    });


    return tagMetrics;
};


/**
 * Get the transform formula for all metrics that has "transform" settings. Return a map of metricId => formula e.g.
 *      {
 *          temperature: "{{value}} * 2",
 *          pressure: "5 + {{value}} * {{pressure}} + {{temperature}}"
 *      }
 */
const getTransformFormulaByMetric = (multipleMetricsDefinitionsSettings: readonly FetchCollectionDataParams[]): {
    [metricId: string]: string | undefined
} => {
    const result: {
        [metricId: string]: string | undefined;
    } = {};

    multipleMetricsDefinitionsSettings.forEach((oneMetricDefSettings) => {

        oneMetricDefSettings.numericMetrics?.forEach((metricSettings) => {
            if (metricSettings.transform) {

                // Get the "formula" configured for the metric.
                const formula = metricSettings.transform.formula;

                // Get all the other placeholders, substrings in this format "{{placeholder_name}}" and add them to the "placeholders" array
                const placeholders: string[] = [];

                // eslint-disable-next-line no-restricted-syntax
                for (const match of formula.matchAll(/{{(.*?)}}/g)) {
                    const placeholder = match[0];
                    // If placeholder is not already added to the array then add it. We don't need to keep track of multiple same placeholders.
                    // It is possible that a formula uses the same placeholder twice e.g. formula = "{{temperature}} * {{temperature}} + 5"
                    // Also, ignore the {{value}} placeholders, we will handle it separately.
                    if (!placeholders.includes(placeholder) && !(/{{\s*value\s*}}/.test(placeholder))) {
                        placeholders.push(placeholder);
                    }
                }

                /**
                 * Now for each placeholder, replace it with a metricId associated with the placeholder. For example
                 *      Replace "{{temperature}}" with the temperature metric's ID. The temperature metric that belongs to the same metrics definition
                 *          as the current metric
                 *      Replace "{{system.battery}}" with the battery metric's ID. The battery metric that belongs to the "system" metrics definition
                 */
                const modifiedFormula = placeholders.reduce((modifiedFormula, placeholder) => {
                    // Get the placeholder's value without the {{}}
                    const placeholderValue = placeholder.replace(/[{}]/g, '');

                    // The placeholderValue could be just a metric's name or combination of metricsDefinitionId and metric name e.g.
                    // "temperature" or "battery" or "environment.temperature" or "system.battery".
                    // If it contains a ".", we will split the string to get the metricsDefinitionId and metricName. If not then we will assume
                    // the entire placeholderValue is the metricName
                    const [metricsDefId, metricName] = placeholderValue.includes('.')
                        ? placeholderValue.split('.')
                        : [undefined, placeholderValue];


                    // If the placeholder contains "metricsDefId" then search for the metricsDefinition from the "multipleMetricsDefinitionsSettings"
                    // array. Else, we use the CURRENT metrics definition we are processing, the "oneMetricDefSettings".
                    const targetMetricsDefinition = metricsDefId
                        ? multipleMetricsDefinitionsSettings.find((param) => {
                            return param.metricsDefinitionId === metricsDefId;
                        })
                        : oneMetricDefSettings;

                    if (targetMetricsDefinition) {
                        // Now we search for the "metricSettings" with the same name as "metricName"
                        const targetMetricSettings = targetMetricsDefinition.numericMetrics?.find((metricSettings) => {
                            return metricSettings.name === metricName;
                        });

                        // If we found a metric setting for the target "metricName" then we can replace the placeholder with the metricSettings' ID
                        // So basically this replace {{temperature}} with {{temperature_metric_id}}
                        // Or replace {{system.battery}} with {{battery_metric_id}}
                        if (targetMetricSettings) {
                            return modifiedFormula.replaceAll(placeholder, `{{${targetMetricSettings.id}}}`);
                        }

                        // Log a warning that the formula is invalid because it tries to use the value of a metric that does not exist
                        console.log(
                            `%cInvalid formula: Metric name '${metricName}' does not exist in metrics definition '${targetMetricsDefinition.metricsDefinitionId}'`,
                            'color:red; font-weight:bold;',
                            metricSettings.transform?.formula,
                        );
                    } else {
                        // Log a warning that the formula is invalid because it tries to use a metrics definition that does not exist
                        console.log(
                            `%cInvalid formula: Metrics definition ID '${metricsDefId}' does not exist.`,
                            'color:red; font-weight:bold;',
                            metricSettings.transform?.formula,
                        );
                    }

                    return modifiedFormula;
                }, formula);

                // Add the formula to the result BUT before that, need to replace the default placeholder "{{value}}" with the current metric's ID.
                result[metricSettings.id] = modifiedFormula.replace(/{{\s*value\s*}}/g, `{{${metricSettings.id}}}`);
            }
        });
    });

    return result;
};



/**
 * Interface for the MultiDataSourcesViewContainer props
 */
export interface MultiDataSourcesViewContainerProps {
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;
    readonly customStyles?: {
        readonly container?: CSSProperties | undefined;
        readonly legendsContainer?: CSSProperties | undefined;
        readonly contentContainer?: CSSProperties | undefined;
    } & Pick<SynchronizedNumericTagMetricsDisplayContainerProps, 'numericChartStyle' | 'tagChartStyle'> | undefined;

    readonly projectId: number;
    readonly chartIdPrefix: string;
    readonly fetchCollectionsDataParams?: readonly FetchCollectionDataParams[] | undefined;
    readonly fetchEventLogDataParams?: readonly FetchEventLogDataParams[] | undefined;
    readonly fetchDataOptions: FetchRawDataOptions | FetchAggregatedDataOptions;

    // OPTIONAL map of metrics that should be selected initially
    readonly initialSelectedMetricIdsMap?: {
        readonly numericMetrics: {
            readonly [metricId: string]: boolean | undefined;
        };
        readonly tagMetrics: {
            readonly [metricId: string]: boolean | undefined;
        };
    } | undefined;

    readonly icon?: string | undefined;
    readonly title?: string | undefined;
    readonly chartViewSettings?: ChartViewSettings | undefined;
    readonly tableViewSettings?: TableViewSettings | undefined;
    readonly showVerticalLineAtTimestamp?: number | undefined;
    readonly highlightedMetrics?: SynchronizedNumericTagMetricsDisplayContainerProps['highlightedMetrics'];
    readonly metricViewSettings?: {
        readonly numericMetrics?: MetricViewSettings | undefined;
        readonly tagMetrics?: MetricViewSettings | undefined;
    };

    // Whether or not to show the CHART view. The default is TRUE unless this is specifically set to false
    readonly showChartView?: boolean | undefined;

    // Whether or not to show TABLE view. The default is FALSE unless this is specifically set to true
    readonly showTableView?: boolean | undefined;

    // OPTIONAL Handler to convert event log to tag chart data. There is a generic handler that will be used if this is not provided.
    // If we have a log events record that can't be handled by the generic handler then the container can pass in a custom handler
    readonly convertEventLogRecordsToTagChartData?: ((
        eventLogData: EventLogData,
        eventLogInfo: FetchEventLogDataParams,
    )=> {
        [metricId: string]: GanttChartDataPoint[];
    }) | undefined;

    readonly convertEventLogRecordsToTagMetrics?: ((
        eventLogData: EventLogData,
        eventLogInfo: FetchEventLogDataParams,
    )=> readonly ChartMetricInfo[]) | undefined;

    // OPTIONAL function that can be used for sorting the numeric and tag metrics. By default, the metrics will not be sorted.
    readonly numericMetricsSorter?: ((metrics: readonly ChartMetricInfo[])=> readonly ChartMetricInfo[]) | undefined;
    readonly tagMetricsSorter?: ((
        metrics: readonly ChartMetricInfo[],
        ganttChartDataPoints: {
            [metricId: string]: GanttChartDataPoint[] | undefined;
        } | undefined
    )=> readonly ChartMetricInfo[]) | undefined;

    // OPTIONAL handler for when a numeric metric series is selected in the chart, when the user clicked on the chart line.
    readonly onNumericMetricSelected?: ((
        metric: ChartMetricInfo | undefined, date: number
    )=> void) | undefined;

    // OPTIONAL handler for when a numeric metric is unselected
    readonly onClearSelectedNumericMetric?: (()=> void) | undefined;


    // OPTIONAL handlers for creating custom labels for the XY chart's Y Axis. The default behavior will just display the Y value and unit
    readonly getNumericMetricChartYAxisHTMLLabel?: (
        (
            text: string | undefined, target: am4charts.AxisLabel, metric: ChartMetricInfo,
            numericMetrics: readonly ChartMetricInfo[],
            tagMetrics: readonly ChartMetricInfo[],
            metricsDataPoints: readonly MultiMetricsDataPoint[] | undefined,
            ganttChartDataPoints: {
                [metricId: string]: readonly GanttChartDataPoint[] | undefined;
            } | undefined
        )=> string | undefined
    ) | undefined;
    readonly getNumericMetricChartYAxisTextLabel?: (
        (
            text: string | undefined, target: am4charts.AxisLabel, metric: ChartMetricInfo,
            numericMetrics: readonly ChartMetricInfo[],
            tagMetrics: readonly ChartMetricInfo[],
            metricsDataPoints: readonly MultiMetricsDataPoint[] | undefined,
            ganttChartDataPoints: {
                [metricId: string]: readonly GanttChartDataPoint[] | undefined;
            } | undefined
        )=> string | undefined
    ) | undefined;

    // OPTIONAL handlers for creating custom labels for the Gantt chart Y Axis. The default behavior will just display the tag metric's displayName
    readonly getTagMetricChartYAxisHTMLLabel?: (
        (
            text: string | undefined, target: am4charts.AxisLabel, metric: ChartMetricInfo,
            numericMetrics: readonly ChartMetricInfo[],
            tagMetrics: readonly ChartMetricInfo[],
            metricsDataPoints: readonly MultiMetricsDataPoint[] | undefined,
            ganttChartDataPoints: {
                [metricId: string]: readonly GanttChartDataPoint[] | undefined;
            } | undefined
        )=> string | undefined
    ) | undefined;
    readonly getTagMetricChartYAxisTextLabel?: (
        (
            text: string | undefined, target: am4charts.AxisLabel, metric: ChartMetricInfo,
            numericMetrics: readonly ChartMetricInfo[],
            tagMetrics: readonly ChartMetricInfo[],
            metricsDataPoints: readonly MultiMetricsDataPoint[] | undefined,
            ganttChartDataPoints: {
                [metricId: string]: readonly GanttChartDataPoint[] | undefined;
            } | undefined
        )=> string | undefined
    ) | undefined;

    // OPTIONAL handlers for creating tooltip for the XY chart's series.
    readonly getNumericMetricChartTooltipText?: (
        (
            text: string | undefined, target: am4charts.XYSeries, metric: ChartMetricInfo,
            numericMetrics: readonly ChartMetricInfo[],
            tagMetrics: readonly ChartMetricInfo[],
            metricsDataPoints: readonly MultiMetricsDataPoint[] | undefined,
            ganttChartDataPoints: {
                [metricId: string]: readonly GanttChartDataPoint[] | undefined;
            } | undefined
        )=> string | undefined
    ) | undefined;
    readonly getNumericMetricChartTooltipHTML?: (
        (
            text: string | undefined, target: am4charts.XYSeries, metric: ChartMetricInfo,
            numericMetrics: readonly ChartMetricInfo[],
            tagMetrics: readonly ChartMetricInfo[],
            metricsDataPoints: readonly MultiMetricsDataPoint[] | undefined,
            ganttChartDataPoints: {
                [metricId: string]: readonly GanttChartDataPoint[] | undefined;
            } | undefined
        )=> string | undefined
    ) | undefined;

    // OPTIONAL handlers for creating tooltip for the XY chart's series.
    readonly getTagMetricChartTooltipText?: (
        (
            text: string | undefined, target: am4charts.XYSeries, metric: ChartMetricInfo,
            numericMetrics: readonly ChartMetricInfo[],
            tagMetrics: readonly ChartMetricInfo[],
            metricsDataPoints: readonly MultiMetricsDataPoint[] | undefined,
            ganttChartDataPoints: {
                [metricId: string]: readonly GanttChartDataPoint[] | undefined;
            } | undefined
        )=> string | undefined
    ) | undefined;
    readonly getTagMetricChartTooltipHTML?: (
        (
            text: string | undefined, target: am4charts.XYSeries, metric: ChartMetricInfo,
            numericMetrics: readonly ChartMetricInfo[],
            tagMetrics: readonly ChartMetricInfo[],
            metricsDataPoints: readonly MultiMetricsDataPoint[] | undefined,
            ganttChartDataPoints: {
                [metricId: string]: readonly GanttChartDataPoint[] | undefined;
            } | undefined
        )=> string | undefined
    ) | undefined;

    // OPTIONAL handler to return the list of export data format the handler supports. If no option is provided, this component will not display
    // any option for the user to export data.
    readonly getSupportedExportDataFormats?: (
        (
            numericMetrics: readonly ChartMetricInfo[],
            tagMetrics: readonly ChartMetricInfo[],
            metricsDataPoints: readonly MultiMetricsDataPoint[] | undefined,
            ganttChartDataPoints: {
                [metricId: string]: readonly GanttChartDataPoint[] | undefined;
            } | undefined
        )=> ReadonlyNonEmptyArray<ExportDataFormat> | undefined
    ) | undefined;

    // OPTIONAL handler to generate the file content for the data to be exported. This component DOES NOT know how to generate the data
    // and it depends on the handler to generate the file content based on the specified format.
    readonly createExportFileContent?: (
        (
            format: string,
            selectedNumericMetrics: readonly ChartMetricInfo[],
            selectedTagMetrics: readonly ChartMetricInfo[],
            numericMetrics: readonly ChartMetricInfo[],
            tagMetrics: readonly ChartMetricInfo[],
            metricsDataPoints: readonly MultiMetricsDataPoint[] | undefined,
            ganttChartDataPoints: {
                [metricId: string]: readonly GanttChartDataPoint[] | undefined;
            } | undefined
        )=> string | undefined
    ) | undefined;
}


/**
 * This component is used for displaying data for multiple collections. Given a set of collection info e.g. the metrics and all  the necessary
 * info for making API call to fetch data for the collection. This component doesn't actually handle displaying the data. It takes care of
 * loading the data, convert the data to some format that the renderer can work with and then pass the data to the renderer. The renderer
 * that this component will use is the SynchronizedNumericTagMetricsDisplayContainer.
 */
export const MultiDataSourcesViewContainer: React.FC<MultiDataSourcesViewContainerProps> = (
    props: MultiDataSourcesViewContainerProps,
) => {
    const { state:dataState } = useContext(ritzDataContext);
    const { state:uiState, dispatch:uiDispatch } = useContext(modeUIContext);
    const project = useSelectProject(props.projectId);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;
    const selectedLanguage = getLanguageByCode(
        SUPPORTED_LANGUAGES, selectLanguage(uiState),
    ) || DEFAULT_LANGUAGE;
    const currentLanguage = loggedInAgent
        ? getPreferredLanguage(project, loggedInAgent, DEFAULT_LANGUAGE)
        : selectedLanguage;

    const { trans } = useRitzTranslation(project);
    const panelClasses = useModePanelStyle();
    const classes = useStyle();


    // Set up the searchParams use for data table pagination
    const {
        searchParams, onItemsPerPageChange, onPrevPage, onNextPage, onPage,
    } = useSearchParams<{}>({
        searchText  : '',
        otherFields : {},
        itemsPerPage: props.tableViewSettings?.defaultPageSize ?? ITEMS_PER_PAGE_100,
        currentPage : 0,
    });



    // The state that contain data for ALL collection merged into 1 object. The data will be separated for Numeric and Tag metric data.
    // Note that this data is formatted to work with SynchronizedNumericTagMetricsDisplayContainer.
    const [globalChartData, setGlobalChartData] = useState<SynchronizedNumericTagMetricsDisplayContainerProps['metricsData']>();


    // Get all the handlers from props
    const {
        convertEventLogRecordsToTagChartData,
        convertEventLogRecordsToTagMetrics,
        numericMetricsSorter,
        tagMetricsSorter,
        onNumericMetricSelected,
        onClearSelectedNumericMetric,
        getNumericMetricChartYAxisTextLabel,
        getNumericMetricChartYAxisHTMLLabel,
        getTagMetricChartYAxisTextLabel,
        getTagMetricChartYAxisHTMLLabel,
        getNumericMetricChartTooltipText,
        getNumericMetricChartTooltipHTML,
        getTagMetricChartTooltipText,
        getTagMetricChartTooltipHTML,
    } = props;


    /**
     * Each collection has an array of numeric and tag metrics. We need to merge them into 1 array of numeric metrics and 1 array of tag metrics.
     * Basically, this will flatten the numeric metrics and tag metrics from all the metrics definitions.
     */
    const allCollectionsMetricsInfo = useMemo((): {
        readonly numericMetrics: readonly ChartMetricInfo[];
        readonly tagMetrics: readonly ChartMetricInfo[];
    } => {
        // Combine all the numeric metrics from all the fetchCollectionsDataParams into 1 array
        const numericMetrics = props.fetchCollectionsDataParams?.map((params) => {
            return params.numericMetrics ?? [];
        }).flat() ?? [];

        // Combine all the tag metrics from all the fetchCollectionsDataParams into 1 array
        const tagMetrics = props.fetchCollectionsDataParams?.map((params) => {
            return params.tagMetrics ?? [];
        }).flat() ?? [];

        return {
            numericMetrics,
            tagMetrics,
        };
    }, [props.fetchCollectionsDataParams]);



    /**
     * For eventLogs's numeric/tag metrics info, we will have to wait until we fetched data. The numeric/tag metrics defined in the "home_app_proxy"
     * metrics definition are NOT the metrics info we will be using for the chart. We will be creating these metrics info dynamically based
     * on the data we fetched.
     */
    const [allDynamicMetricsInfo, setAllDynamicMetricsInfo] = useState<{
        readonly numericMetrics: readonly ChartMetricInfo[];
        readonly tagMetrics: readonly ChartMetricInfo[];
    }>({
        numericMetrics: [],
        tagMetrics    : [],
    });


    /**
     * Create ALL metrics info by combining the numeric/tag metrics from TSDB metrics definitions and the numeric/tag metrics info created
     * dynamically from the data
     */
    const allMetricsInfo = useMemo((): {
        readonly numericMetrics: readonly ChartMetricInfo[];
        readonly tagMetrics: readonly ChartMetricInfo[];
    } => {
        const allNumericMetrics = [...allCollectionsMetricsInfo.numericMetrics, ...allDynamicMetricsInfo.numericMetrics];
        const sortedNumericMetrics = numericMetricsSorter
            ? numericMetricsSorter(allNumericMetrics)
            : allNumericMetrics;

        const allTagMetrics = [...allCollectionsMetricsInfo.tagMetrics, ...allDynamicMetricsInfo.tagMetrics];
        const sortedTagMetrics = tagMetricsSorter
            ? tagMetricsSorter(allTagMetrics, globalChartData?.ganttChartDataPoints)
            : allTagMetrics;

        return {
            numericMetrics: sortedNumericMetrics,
            tagMetrics    : sortedTagMetrics,
        };
    }, [allCollectionsMetricsInfo.numericMetrics, allCollectionsMetricsInfo.tagMetrics, allDynamicMetricsInfo.numericMetrics,
        allDynamicMetricsInfo.tagMetrics, globalChartData?.ganttChartDataPoints, numericMetricsSorter, tagMetricsSorter]);


    // The state to keep track of the IDs of the metrics the user selected
    // Initialize it with the "initialSelectedMetricIdsMap"
    const [selectedMetricIdsMap, setSelectedMetricIdsMap] = useState<{
        readonly numericMetrics: {
            readonly [metricId: string]: boolean | undefined;
        };
        readonly tagMetrics: {
            readonly [metricId: string]: boolean | undefined;
        };
    }>(props.initialSelectedMetricIdsMap ?? {
        numericMetrics: {},
        tagMetrics    : {},
    });


    // The state to keep track of the actual Metric objects of the metrics the user selected.
    const selectedMetricsInfo = useMemo((): typeof allMetricsInfo => {
        return {
            numericMetrics: allMetricsInfo.numericMetrics.filter((metric) => {
                return selectedMetricIdsMap.numericMetrics[metric.id];
            }),
            tagMetrics: allMetricsInfo.tagMetrics.filter((metric) => {
                return selectedMetricIdsMap.tagMetrics[metric.id];
            }),
        };
    }, [allMetricsInfo.numericMetrics, allMetricsInfo.tagMetrics, selectedMetricIdsMap.numericMetrics, selectedMetricIdsMap.tagMetrics]);



    /**
     * Sometimes, the metrics the user selected DOES NOT have data for the selected date range. If a metric does not have data, we can optimize
     * the UI by not displaying the legends for these metrics. We also don't need to create y axes and series these metrics as well.
     * Therefore, this function will go through the list of selectedMetricsInfo and filter out the metrics that don't have data.
     */
    const selectedMetricsInfoWithData = useMemo((): {
        readonly numericMetrics: readonly ChartMetricInfo[];
        readonly tagMetrics: readonly ChartMetricInfo[];
    } => {
        const numericMetricsWithData = props.metricViewSettings?.numericMetrics?.hideMetricsWithoutData === false
            // User DOES NOT want to hide metrics without data therefore, return all metrics from selectedMetricsInfo
            ? selectedMetricsInfo.numericMetrics
            // User want to hide metrics without data therefore, filter out metrics without data
            : selectedMetricsInfo.numericMetrics.filter((metric) => {
                return globalChartData?.metricsDataPoints?.some((dataPoint: MultiMetricsDataPoint) => {
                    return dataPoint[metric.id] !== undefined && dataPoint[metric.id] !== null;
                });
            });

        const tagMetricsWithData = props.metricViewSettings?.tagMetrics?.hideMetricsWithoutData
            // User DOES NOT want to hide metrics without data therefore, return all metrics from selectedMetricsInfo
            ? selectedMetricsInfo.tagMetrics
            // User want to hide metrics without data therefore, filter out metrics without data
            : selectedMetricsInfo.tagMetrics.filter((metric) => {
                const ganttChartDataPoints = globalChartData?.ganttChartDataPoints[metric.id];
                return ganttChartDataPoints !== undefined && ganttChartDataPoints.length > 0;
            });

        return {
            numericMetrics: numericMetricsWithData,
            tagMetrics    : tagMetricsWithData,
        };
    }, [globalChartData?.metricsDataPoints, globalChartData?.ganttChartDataPoints, props.metricViewSettings?.numericMetrics?.hideMetricsWithoutData,
        props.metricViewSettings?.tagMetrics?.hideMetricsWithoutData, selectedMetricsInfo.numericMetrics, selectedMetricsInfo.tagMetrics]);



    /**
     * Whenever allMetricsInfo changed, we need to automatically update the selectedMetricIdsMap. Sometimes new metrics get added and
     * sometime old metrics are removed. This can happen because of the dynamic metrics.
     */
    useEffect(() => {
        setSelectedMetricIdsMap((currentMap) => {
            const maxSelectedNumericMetrics = props.metricViewSettings?.numericMetrics?.maxInitiallySelected ?? Number.MAX_SAFE_INTEGER;
            const maxSelectedTagMetrics = props.metricViewSettings?.tagMetrics?.maxInitiallySelected ?? Number.MAX_SAFE_INTEGER;

            const newSelectedNumericMetricIdsMap = {};

            // The current selectedTagMetricsCount is the number of metrics in the "numericMetrics" that are set to true
            let selectedNumericMetricsCount = Object.values(currentMap.numericMetrics).filter((value) => {
                return value === true;
            }).length;

            allMetricsInfo.numericMetrics.forEach((metric) => {
                if (currentMap.numericMetrics[metric.id] !== undefined) {
                    // If the metric exist in the currentMap then keep the same state it has in the current map
                    newSelectedNumericMetricIdsMap[metric.id] = currentMap.numericMetrics[metric.id];
                } else if (selectedNumericMetricsCount < maxSelectedNumericMetrics) {
                    // If not, it must be a new metric so set it to true if the current number of selected metrics is still under the limit
                    newSelectedNumericMetricIdsMap[metric.id] = true;
                    selectedNumericMetricsCount += 1;
                } else {
                    // Already passed the limit, set the metric's state to false, not selected
                    newSelectedNumericMetricIdsMap[metric.id] = false;
                }
            });

            const newSelectedTagMetricIdsMap = {};

            // The current selectedTagMetricsCount is the number of metrics in the "tagMetrics" that are set to true
            let selectedTagMetricsCount = Object.values(currentMap.tagMetrics).filter((value) => {
                return value === true;
            }).length;
            allMetricsInfo.tagMetrics.forEach((metric) => {
                if (currentMap.tagMetrics[metric.id] !== undefined) {
                    // If the metric exist in the currentMap then keep the same state it has in the current map
                    newSelectedTagMetricIdsMap[metric.id] = currentMap.tagMetrics[metric.id];
                } else if (selectedTagMetricsCount < maxSelectedTagMetrics) {
                    // If not, it must be a new metric so set it to true if the current number of selected metrics is still under the limit
                    newSelectedTagMetricIdsMap[metric.id] = true;
                    selectedTagMetricsCount += 1;
                } else {
                    // Already passed the limit, se the metric's state to false, not selected
                    newSelectedTagMetricIdsMap[metric.id] = false;
                }
            });

            return {
                numericMetrics: newSelectedNumericMetricIdsMap,
                tagMetrics    : newSelectedTagMetricIdsMap,
            };
        });
    }, [allMetricsInfo, props.metricViewSettings?.numericMetrics?.maxInitiallySelected, props.metricViewSettings?.tagMetrics?.maxInitiallySelected]);



    /**
     * Fetch TSDB collection data, convert and return the data to format that the SynchronizedNumericTagMetricsDisplayContainer can understand
     */
    const fetchCollectionsData = useCallback(async (
        fetchCollectionsParams: readonly FetchCollectionDataParams[],
        fetchDataOptions: FetchRawDataOptions | FetchAggregatedDataOptions,
    ): Promise<SynchronizedNumericTagMetricsDisplayContainerProps['metricsData']> => {

        // Create API calls to fetch data for each collection and put them into an array so we can dispatch them all in 1 call
        const getDataAPICalls = fetchCollectionsParams.map((fetchCollectionDataParams) => {
            const valueNames = fetchCollectionDataParams.numericMetrics?.map((metric) => {
                return metric.name;
            });
            const tagNames = fetchCollectionDataParams.tagMetrics?.map((metric) => {
                return metric.name;
            });
            if (isFetchRawDataOptions(fetchDataOptions)) {
                // Check the "fetchDataOptions.limit". If it is NEGATIVE number then fetch data starting from the "endTime". If it is POSITIVE
                // then fetch data starting from the "startTime"
                const timestamp = fetchDataOptions.limit < 0 ? fetchDataOptions.endTime : fetchDataOptions.startTime;
                return AppAPI.getInstance().getTimeSeriesCollectionRawData(
                    fetchCollectionDataParams.homeId, fetchCollectionDataParams.smartModuleId, fetchCollectionDataParams.collectionId,
                    (new Date(timestamp)).toISOString(), fetchDataOptions.limit,
                    valueNames ?? [],
                    tagNames ?? [],
                );
            }
            return AppAPI.getInstance().getTimeSeriesCollectionData(
                fetchCollectionDataParams.homeId, fetchCollectionDataParams.smartModuleId, fetchCollectionDataParams.collectionId,
                (new Date(fetchDataOptions.startTime)).toISOString(),
                (new Date(fetchDataOptions.endTime)).toISOString(),
                valueNames ?? [],
                fetchDataOptions.aggregation, fetchDataOptions.resolution,
            );
        });


        // All the data from all the collections will be merged and stored in this object.
        const mergedCollectionsData: {
            timestampToDataPointMap: { [timestamp: string]: MultiMetricsDataPoint | undefined; };
            dataPoints: MultiMetricsDataPoint[];
        } = {
            timestampToDataPointMap: {},
            dataPoints             : [],
        };

        // Gantt chart required specific data format so we will format all Collections' tag metric data into the format that can be displayed in
        // Gantt chart and put them all into this object.
        const mergedCollectionsGanttChartData: {
            [metricId: string]: GanttChartDataPoint[];
        } = {};


        // Make the API call and handle the response.
        await Promise.allSettled(getDataAPICalls).then((responses) => {
            responses.forEach((response, index) => {
                if (response.status === 'fulfilled') {
                    const fetchCollectionDataParams = fetchCollectionsParams[index];
                    const collectionData = response.value;

                    // Get numeric and tag metrics values from the collection data and add them to the mergedCollectionsData
                    mergeCollectionDataToGlobalChartData(
                        collectionData, fetchCollectionDataParams, mergedCollectionsData, fetchDataOptions.startTime, fetchDataOptions.endTime,
                    );

                    // Create data points for the tag metrics values from the collection and add them to the mergedCollectionsTagData
                    mergeCollectionTagDataToGlobalGanttChartData(collectionData, fetchCollectionDataParams, mergedCollectionsGanttChartData);
                }
            });
        });

        const sortedMergedCollectionData = mergedCollectionsData.dataPoints.sort((dataRow1, dataRow2) => {
            return dataRow1.dataPointTimestamp - dataRow2.dataPointTimestamp;
        });


        /**
         * Each metric can have a transform data settings. Instead of transforming the data 1 metric at a time, we can do it for
         * all the metrics that are in the same collection. So this will collect all the metrics that require data transformation
         * and put them into a map of metricName => formula. Lets say a collection has 5 metrics but only the temperature and pressure
         * has some transformation settings. Then this will be the result of the "transformFormulaByMetricName"
         *      transformFormulaByMetricName = {
         *          temperature: "{{value}} * 5 + 123",
         *          pressure: "(5 * {{value}}) / 100"
         *      }
         */
        const transformFormulaByMetricName = getTransformFormulaByMetric(fetchCollectionsParams);
        const transformedCollectionData = transformMultiMetricsData(sortedMergedCollectionData, transformFormulaByMetricName);


        return {
            metricsDataPoints   : transformedCollectionData,
            ganttChartDataPoints: mergedCollectionsGanttChartData,
        };
    }, []);



    /**
     * Fetch event logs data. Convert and return the data in a format that the SynchronizedNumericTagMetricsDisplayContainer can
     * understand.
     *
     * IMPORTANT NOTE: This function will not only fetch and format event log data to data that SynchronizedNumericTagMetricsDisplayContainer
     * can work with but it will also return the numeric/tag metrics info that were dynamically created based on the even log data.
     */
    const fetchEventLogsData = useCallback(async (
        fetchEventLogsParams: readonly FetchEventLogDataParams[],
        fetchDataOptions: FetchAggregatedDataOptions,
    ): Promise<{
        readonly metrics: {
            readonly numericMetrics: readonly ChartMetricInfo[];
            readonly tagMetrics: readonly ChartMetricInfo[];
        },
        readonly metricsData: SynchronizedNumericTagMetricsDisplayContainerProps['metricsData']
    }> => {

        // Create API calls to fetch data for each event log param and put them into an array so we can dispatch them all in 1 call
        const getDataAPICalls = fetchEventLogsParams.map((fetchEventLogDataParams) => {
            return ModeAppProxyAPI.getInstance().getEventLog(
                fetchEventLogDataParams.homeId,
                fetchEventLogDataParams.logSourcePath,
                {
                    searchKeys: fetchEventLogDataParams.searchKeys,
                    begin     : (new Date(fetchDataOptions.startTime)).toISOString(),
                    end       : (new Date(fetchDataOptions.endTime)).toISOString(),
                },
            );
        });


        // All the dynamically created numeric and tag metrics info will be combined and added to this object
        const combinedDynamicMetrics: {
            numericMetrics: ChartMetricInfo[];
            tagMetrics: ChartMetricInfo[];
        } = {
            numericMetrics: [],
            tagMetrics    : [],
        };

        // All the metric data will be added to this object
        const combinedEventLogData: SynchronizedNumericTagMetricsDisplayContainerProps['metricsData'] = {
            metricsDataPoints   : [],
            ganttChartDataPoints: {},
        };



        // Make the API call and handle the response.
        await Promise.allSettled(getDataAPICalls).then((responses) => {
            responses.forEach((response, index) => {
                if (response.status === 'fulfilled') {
                    const fetchEventLogDataParams = fetchEventLogsParams[index];
                    const logData = response.value;

                    if (logData) {
                        // NOTE: convertEventLogRecordsToGlobalTagChartData will not only convert logData but it will also dynamically create and
                        // return an array of metrics based on the event log data

                        // Calling a adapter function can fail because we don't know if it does the right thing so we need to try/catch
                        try {
                            const tagData = convertEventLogRecordsToTagChartData
                                // Use the convertEventLogRecordsToTagChartData function implemented by the adapter if there is one
                                ? convertEventLogRecordsToTagChartData(
                                    logData, fetchEventLogDataParams,
                                )
                                // Else use the genericConvertEventLogRecordsToTagChartData
                                : genericConvertEventLogRecordsToTagChartData(logData, fetchEventLogDataParams);

                            const tagMetrics = convertEventLogRecordsToTagMetrics
                                // Use the convertEventLogRecordsToTagMetrics function implemented by the adapter if there is one
                                ? convertEventLogRecordsToTagMetrics(
                                    logData, fetchEventLogDataParams,
                                )
                                // Else use the genericConvertEventLogRecordsToTagMetrics
                                : genericConvertEventLogRecordsToTagMetrics(logData, fetchEventLogDataParams);

                            // Add the tag metrics created by the convertEventLogRecordsToGlobalTagChartData to the combinedDynamicTagMetrics
                            combinedDynamicMetrics.tagMetrics.push(...tagMetrics);

                            // Add the dataPoints created by the convertEventLogRecordsToGlobalTagChartData to the combinedEventLogData
                            Object.entries(tagData).forEach(([metricId, dataPointsArray]) => {
                                combinedEventLogData.ganttChartDataPoints[metricId] = dataPointsArray;
                            });
                        } catch (error) {
                            console.error(error);
                        }
                    }
                }
            });
        });

        // Return BOTH the new Numeric/Tag metrics dynamically created by this function and also the metric's data
        return {
            metrics    : combinedDynamicMetrics,
            metricsData: combinedEventLogData,
        };
    }, [convertEventLogRecordsToTagChartData, convertEventLogRecordsToTagMetrics]);



    /**
     * Fetch ALL data, collections and event logs
     */
    const fetchData = useCallback(async (
        fetchCollectionsParams: readonly FetchCollectionDataParams[] | undefined,
        fetchEventLogsParams: readonly FetchEventLogDataParams[] | undefined,
        fetchDataOptions: FetchRawDataOptions | FetchAggregatedDataOptions,
    ): Promise<{
        readonly dynamicMetrics: {
            readonly numericMetrics: readonly ChartMetricInfo[];
            readonly tagMetrics: readonly ChartMetricInfo[];
        },
        readonly metricsData: SynchronizedNumericTagMetricsDisplayContainerProps['metricsData']
    }> => {

        // The combined metrics that we created dynamically based on the data
        const combinedDynamicMetrics: {
            readonly numericMetrics: ChartMetricInfo[];
            readonly tagMetrics: ChartMetricInfo[];
        } = {
            numericMetrics: [],
            tagMetrics    : [],
        };


        // The combined data from all data sources
        const combinedData: SynchronizedNumericTagMetricsDisplayContainerProps['metricsData'] = {
            metricsDataPoints   : [],
            ganttChartDataPoints: {},
        };

        if (fetchCollectionsParams) {
            // fetch data from collection data source and add the result to the combinedData
            const collectionsData = await fetchCollectionsData(fetchCollectionsParams, fetchDataOptions);
            combinedData.metricsDataPoints.push(...collectionsData.metricsDataPoints);

            // Merge the ganttChartDataPoints from the new collectionsData into the combinedData object
            Object.entries(collectionsData.ganttChartDataPoints).forEach(([metricId, dataPointsArray]) => {
                combinedData.ganttChartDataPoints[metricId] = dataPointsArray;
            });
        }

        // For "home_app_proxy" data source, we can only support aggregated data options which requires start/end time. We can't
        // fetch data from home_app_proxy with "rawDataOptions" because it has 1 timestamp and data count.
        // Therefore, if the fetchDataOptions is FetchRawDataOptions, home_app_proxy data will not be displayed.
        if (fetchEventLogsParams && isFetchAggregatedDataOptions(fetchDataOptions)) {
            // fetch data from home_app_proxy data source and add the result to the combinedData. Note that the result contains both
            // data and dynamically created numeric/tag metrics
            const eventLogsDataResult = await fetchEventLogsData(fetchEventLogsParams, fetchDataOptions);

            // Add event log data points into the combinedData
            combinedData.metricsDataPoints.push(...eventLogsDataResult.metricsData.metricsDataPoints);

            // Merge the ganttChartDataPoints from the new eventLogsDataResult.metricsData into the combinedData object
            Object.entries(eventLogsDataResult.metricsData.ganttChartDataPoints).forEach(([metricId, dataPointsArray]) => {
                combinedData.ganttChartDataPoints[metricId] = dataPointsArray;
            });

            // fetchEventLogsData also return the numeric/tag metrics object that were created based on the event log data.
            // These will be the actual numeric/tag metric we will be passing to the chart, NOT the numeric/tag metrics defined in
            // the metricsDefinition for the "home_app_proxy"
            combinedDynamicMetrics.numericMetrics.push(...eventLogsDataResult.metrics.numericMetrics);
            combinedDynamicMetrics.tagMetrics.push(...eventLogsDataResult.metrics.tagMetrics);
        }

        return {
            dynamicMetrics: combinedDynamicMetrics,
            metricsData   : combinedData,
        };
    }, [fetchCollectionsData, fetchEventLogsData]);



    useEffect(() => {
        fetchData(props.fetchCollectionsDataParams, props.fetchEventLogDataParams, props.fetchDataOptions).then((response) => {
            setGlobalChartData(response.metricsData);
            setAllDynamicMetricsInfo(response.dynamicMetrics);
        }).catch((error) => {
            // eslint-disable-next-line no-console
            console.error(error);
        });
    }, [fetchData, props.fetchCollectionsDataParams, props.fetchDataOptions, props.fetchEventLogDataParams]);



    const onNumericMetricSeriesClickedHandler = useCallback((metricId: string, date: number) => {
        if (onNumericMetricSelected) {
            const numericMetric = allMetricsInfo.numericMetrics.find((metric) => {
                return metric.id === metricId;
            });
            onNumericMetricSelected(numericMetric, date);
        }
    }, [allMetricsInfo.numericMetrics, onNumericMetricSelected]);


    /**
     * Capture the event from the SynchronizedNumericTagMetricsDisplayContainer and then forward the event to the adapters. We need to do this
     * instead of directly calling the adapter function because we need to pass additional data to the adapters
     */
    const getNumericMetricChartYAxisTextLabelProxy = useCallback((
        text: string | undefined, targetAxis: am4charts.AxisLabel, targetMetric: ChartMetricInfo,
    ): string | undefined => {
        return getNumericMetricChartYAxisTextLabel?.(
            text, targetAxis, targetMetric,
            allMetricsInfo.numericMetrics, allMetricsInfo.tagMetrics,
            globalChartData?.metricsDataPoints, globalChartData?.ganttChartDataPoints,
        );
    }, [allMetricsInfo.numericMetrics, allMetricsInfo.tagMetrics, getNumericMetricChartYAxisTextLabel, globalChartData?.metricsDataPoints,
        globalChartData?.ganttChartDataPoints]);


    const getNumericMetricChartYAxisHTMLLabelProxy = useCallback((
        text: string | undefined, targetAxis: am4charts.AxisLabel, targetMetric: ChartMetricInfo,
    ): string | undefined => {
        return getNumericMetricChartYAxisHTMLLabel?.(
            text, targetAxis, targetMetric,
            allMetricsInfo.numericMetrics, allMetricsInfo.tagMetrics,
            globalChartData?.metricsDataPoints, globalChartData?.ganttChartDataPoints,
        );
    }, [allMetricsInfo.numericMetrics, allMetricsInfo.tagMetrics, getNumericMetricChartYAxisHTMLLabel, globalChartData?.metricsDataPoints,
        globalChartData?.ganttChartDataPoints]);


    const getTagMetricChartYAxisTextLabelProxy = useCallback((
        text: string | undefined, targetAxis: am4charts.AxisLabel, targetMetric: ChartMetricInfo,
    ): string | undefined => {
        return getTagMetricChartYAxisTextLabel?.(
            text, targetAxis, targetMetric,
            allMetricsInfo.numericMetrics, allMetricsInfo.tagMetrics,
            globalChartData?.metricsDataPoints, globalChartData?.ganttChartDataPoints,
        );
    }, [allMetricsInfo.numericMetrics, allMetricsInfo.tagMetrics, getTagMetricChartYAxisTextLabel, globalChartData?.metricsDataPoints,
        globalChartData?.ganttChartDataPoints]);


    const getTagMetricChartYAxisHTMLLabelProxy = useCallback((
        text: string | undefined, targetAxis: am4charts.AxisLabel, targetMetric: ChartMetricInfo,
    ): string | undefined => {
        return getTagMetricChartYAxisHTMLLabel?.(
            text, targetAxis, targetMetric,
            allMetricsInfo.numericMetrics, allMetricsInfo.tagMetrics,
            globalChartData?.metricsDataPoints, globalChartData?.ganttChartDataPoints,
        );
    }, [allMetricsInfo.numericMetrics, allMetricsInfo.tagMetrics, getTagMetricChartYAxisHTMLLabel, globalChartData?.metricsDataPoints,
        globalChartData?.ganttChartDataPoints]);


    const getNumericMetricChartTooltipTextProxy = useCallback((
        text: string | undefined, targetSeries: am4charts.XYSeries, targetMetric: ChartMetricInfo,
    ): string | undefined => {
        return getNumericMetricChartTooltipText?.(
            text, targetSeries, targetMetric,
            allMetricsInfo.numericMetrics, allMetricsInfo.tagMetrics,
            globalChartData?.metricsDataPoints, globalChartData?.ganttChartDataPoints,
        );
    }, [allMetricsInfo.numericMetrics, allMetricsInfo.tagMetrics, getNumericMetricChartTooltipText, globalChartData?.metricsDataPoints,
        globalChartData?.ganttChartDataPoints]);


    const getNumericMetricChartTooltipHTMLProxy = useCallback((
        text: string | undefined, targetSeries: am4charts.XYSeries, targetMetric: ChartMetricInfo,
    ): string | undefined => {
        return getNumericMetricChartTooltipHTML?.(
            text, targetSeries, targetMetric,
            allMetricsInfo.numericMetrics, allMetricsInfo.tagMetrics,
            globalChartData?.metricsDataPoints, globalChartData?.ganttChartDataPoints,
        );
    }, [allMetricsInfo.numericMetrics, allMetricsInfo.tagMetrics, getNumericMetricChartTooltipHTML, globalChartData?.metricsDataPoints,
        globalChartData?.ganttChartDataPoints]);

    const getTagMetricChartTooltipTextProxy = useCallback((
        text: string | undefined, targetSeries: am4charts.XYSeries, targetMetric: ChartMetricInfo,
    ): string | undefined => {
        return getTagMetricChartTooltipText?.(
            text, targetSeries, targetMetric,
            allMetricsInfo.numericMetrics, allMetricsInfo.tagMetrics,
            globalChartData?.metricsDataPoints, globalChartData?.ganttChartDataPoints,
        );
    }, [allMetricsInfo.numericMetrics, allMetricsInfo.tagMetrics, getTagMetricChartTooltipText, globalChartData?.metricsDataPoints,
        globalChartData?.ganttChartDataPoints]);


    const getTagMetricChartTooltipHTMLProxy = useCallback((
        text: string | undefined, targetSeries: am4charts.XYSeries, targetMetric: ChartMetricInfo,
    ): string | undefined => {
        return getTagMetricChartTooltipHTML?.(
            text, targetSeries, targetMetric,
            allMetricsInfo.numericMetrics, allMetricsInfo.tagMetrics,
            globalChartData?.metricsDataPoints, globalChartData?.ganttChartDataPoints,
        );
    }, [allMetricsInfo.numericMetrics, allMetricsInfo.tagMetrics, getTagMetricChartTooltipHTML, globalChartData?.metricsDataPoints,
        globalChartData?.ganttChartDataPoints]);


    /**
     * Create and cache the chart settings to be passed to the SynchronizedNumericTagMetricsDisplayContainer
     */
    const chartSettings = useMemo((): ExtChartSettings => {
        return {
            locale                             : currentLanguage.locale2,
            getNumericMetricChartYAxisTextLabel: getNumericMetricChartYAxisTextLabel ? getNumericMetricChartYAxisTextLabelProxy : undefined,
            getNumericMetricChartYAxisHTMLLabel: getNumericMetricChartYAxisHTMLLabel ? getNumericMetricChartYAxisHTMLLabelProxy : undefined,
            getTagMetricChartYAxisTextLabel    : getTagMetricChartYAxisTextLabel ? getTagMetricChartYAxisTextLabelProxy : undefined,
            getTagMetricChartYAxisHTMLLabel    : getTagMetricChartYAxisHTMLLabel ? getTagMetricChartYAxisHTMLLabelProxy : undefined,
            getNumericMetricChartTooltipText   : getNumericMetricChartTooltipText ? getNumericMetricChartTooltipTextProxy : undefined,
            getNumericMetricChartTooltipHTML   : getNumericMetricChartTooltipHTML ? getNumericMetricChartTooltipHTMLProxy : undefined,
            getTagMetricChartTooltipText       : getTagMetricChartTooltipText ? getTagMetricChartTooltipTextProxy : undefined,
            getTagMetricChartTooltipHTML       : getTagMetricChartTooltipText ? getTagMetricChartTooltipHTMLProxy : undefined,
        };
    }, [currentLanguage.locale2, getNumericMetricChartYAxisTextLabel, getNumericMetricChartYAxisTextLabelProxy, getNumericMetricChartYAxisHTMLLabel,
        getNumericMetricChartYAxisHTMLLabelProxy, getTagMetricChartYAxisTextLabel, getTagMetricChartYAxisTextLabelProxy,
        getTagMetricChartYAxisHTMLLabel, getTagMetricChartYAxisHTMLLabelProxy, getNumericMetricChartTooltipText,
        getNumericMetricChartTooltipTextProxy, getNumericMetricChartTooltipHTML, getNumericMetricChartTooltipHTMLProxy,
        getTagMetricChartTooltipText, getTagMetricChartTooltipTextProxy, getTagMetricChartTooltipHTMLProxy]);


    const texts = useMemo((): SynchronizedNumericTagMetricsDisplayContainerProps['texts'] => {
        return {
            fromLabel : capitalizeAll(trans('label.start')),
            toLabel   : capitalizeAll(trans('label.end')),
            valueLabel: capitalizeAll(trans('label.value')),
        };
    }, [trans]);


    /**
     * Get the absolute start/end date from all the numeric and tag metrics data. We will use this range for the X axes' min/max
     * to make sure all the X axes have the same range.
     */
    const chartDateRange = useMemo((): SynchronizedNumericTagMetricsDisplayContainerProps['dateRange'] => {
        // If props.fetchDataOptions is a FetchAggregatedDataOptions then we should use the startTime/endTime specified in the options
        // as the chart's X axis's date range. This way the chart's X axis date range matches the selected date range in the time control.
        if (isFetchAggregatedDataOptions(props.fetchDataOptions)) {
            return {
                start: props.fetchDataOptions.startTime,
                end  : props.fetchDataOptions.endTime,
            };
        }

        // Else, we set the chart's start/end date range based on the data
        if (globalChartData?.metricsDataPoints || globalChartData?.ganttChartDataPoints) {
            let startDate = Number.MAX_SAFE_INTEGER;
            let endDate = Number.MIN_SAFE_INTEGER;

            if (globalChartData?.metricsDataPoints) {
                globalChartData?.metricsDataPoints.forEach((dataPoint) => {
                    startDate = Math.min(startDate, dataPoint.dataPointTimestamp);
                    endDate = Math.max(endDate, dataPoint.dataPointTimestamp);
                });
            }

            if (globalChartData?.ganttChartDataPoints) {
                Object.values(globalChartData.ganttChartDataPoints).forEach((dataPointsArray) => {
                    dataPointsArray?.forEach((dataPoint) => {
                        startDate = Math.min(startDate, dataPoint.startDate);
                        endDate = Math.max(endDate, dataPoint.endDate);
                    });
                });
            }

            return {
                start: startDate, end: endDate,
            };
        }

        // We don't have data yet, return 1 day range
        return {
            start: Date.now() - Time.DAY_IN_MS, end: Date.now(),
        };
    }, [globalChartData?.metricsDataPoints, globalChartData?.ganttChartDataPoints, props.fetchDataOptions]);


    /**
     * Get the list of data format the handler supports
     */
    const supportedExportDataFormats = useMemo(() => {
        return props.getSupportedExportDataFormats?.(
            allMetricsInfo.numericMetrics, allMetricsInfo.tagMetrics, globalChartData?.metricsDataPoints, globalChartData?.ganttChartDataPoints,
        );
    }, [allMetricsInfo.numericMetrics, allMetricsInfo.tagMetrics, globalChartData?.metricsDataPoints, globalChartData?.ganttChartDataPoints, props]);


    /**
     * This function will be called when the user is done updating the view settings and click OK.
     */
    const onViewOptionsChanged = useCallback((options: {
        newSelectedMetricIdsMap: {
            readonly numericMetrics: {
                readonly [metricId: string]: boolean | undefined;
            };
            readonly tagMetrics: {
                readonly [metricId: string]: boolean | undefined;
            };
        } | undefined;
    }) => {
        if (options.newSelectedMetricIdsMap) {
            setSelectedMetricIdsMap(options.newSelectedMetricIdsMap);
        }
    }, []);


    /**
     * On "Options" button clicked, we will dispatch a UI action to show the slide out panel of this type
     * MultiDataSourcesSlideOutPanelType.VIEW_OPTIONS.
     */
    const onShowViewOptionsButtonClick = useCallback(() => {
        const slideOutPanelOptions: MultiDataSourcesViewOptions = {
            location : SlideOutPanelLocation.RIGHT,
            compType : MultiDataSourcesSlideOutPanelType.VIEW_OPTIONS,
            compProps: {
                data: {
                    projectId                  : props.projectId,
                    metrics                    : allMetricsInfo,
                    allowUnselectNumericMetrics: props.metricViewSettings?.numericMetrics?.allowUnselect,
                    allowUnselectTagMetrics    : props.metricViewSettings?.tagMetrics?.allowUnselect,
                    selectedMetricIdsMap,
                    onViewOptionsChanged,
                },
            },
        };
        uiDispatch(showSlideOutPanel(slideOutPanelOptions));
    }, [allMetricsInfo, onViewOptionsChanged, props.metricViewSettings?.numericMetrics?.allowUnselect,
        props.metricViewSettings?.tagMetrics?.allowUnselect, props.projectId, selectedMetricIdsMap, uiDispatch]);



    /**
     * On "onExportSelectedMetrics" event from the MultiDataSourcesSlideOutPanelType.EXPORT_DATA, we will process the data for the selected
     * metrics and create a file for the user to download.
     */
    const exportSelectedMetrics = useCallback((
        selectedDataFormat: ExportDataFormat,
        selectedFileName: String,
        selectedNumericMetrics: readonly ChartMetricInfo[],
        selectedTagMetrics: readonly ChartMetricInfo[],
    ) => {
        if (props.createExportFileContent) {
            const content = props.createExportFileContent(
                selectedDataFormat.format, selectedNumericMetrics, selectedTagMetrics,
                allMetricsInfo.numericMetrics, allMetricsInfo.tagMetrics, globalChartData?.metricsDataPoints, globalChartData?.ganttChartDataPoints,
            );
            if (content) {
                const ext = selectedDataFormat.fileExtension.startsWith('.')
                    ? selectedDataFormat.fileExtension : `.${selectedDataFormat.fileExtension}`;
                const fileNameWithExt = `${selectedFileName}${selectedFileName.endsWith(ext) ? '' : ext}`;
                const blob = new Blob([content], { type: 'text/octet-stream' });
                const anchor = document.createElement('A') as HTMLAnchorElement;
                anchor.download = fileNameWithExt;
                anchor.setAttribute('target', 'blank');
                anchor.setAttribute('href', window.URL.createObjectURL(blob));
                anchor.click();
            }
        }
    }, [allMetricsInfo.numericMetrics, allMetricsInfo.tagMetrics, globalChartData?.metricsDataPoints, globalChartData?.ganttChartDataPoints, props]);



    /**
     * On "Export" button clicked, we will dispatch a UI action to show the slide out panel of this type
     * MultiDataSourcesSlideOutPanelType.EXPORT_DATA_OPTIONS.
     */
    const onExportButtonClick = useCallback(() => {
        if (props.createExportFileContent && supportedExportDataFormats) {
            const slideOutPanelOptions: MultiDataSourcesExportDataCompOptions = {
                location : SlideOutPanelLocation.RIGHT,
                compType : MultiDataSourcesSlideOutPanelType.EXPORT_DATA_OPTIONS,
                compProps: {
                    data: {
                        projectId              : props.projectId,
                        numericMetrics         : allMetricsInfo.numericMetrics,
                        tagMetrics             : allMetricsInfo.tagMetrics,
                        supportedDataFormats   : supportedExportDataFormats,
                        onExportSelectedMetrics: exportSelectedMetrics,
                    },
                },
            };
            uiDispatch(showSlideOutPanel(slideOutPanelOptions));
        }
    }, [allMetricsInfo.numericMetrics, allMetricsInfo.tagMetrics, exportSelectedMetrics, props.createExportFileContent, props.projectId,
        supportedExportDataFormats, uiDispatch]);


    /**
     * Create the array of drop down menu options for this comp
     */
    const compDropDownOptions = useMemo((): CompPanelHeaderProps['customSecondaryActions'] => {
        const actions: (BaseCompAction | BaseCompSubAction)[] = [{
            label  : capitalizeAll(trans('label.option', { count: 2 })),
            icon   : 'tune',
            onClick: onShowViewOptionsButtonClick,
        }];

        // If there is a highlighted numeric metrics AND there is a handler for clearing the highlighted metric then add an action to clear
        if (Object.values(props.highlightedMetrics?.numericMetrics ?? {}).length > 0 && onClearSelectedNumericMetric) {
            actions.push({
                label  : capitalizeAll(trans('label.clear_highlighted_metric')),
                icon   : 'clear',
                onClick: onClearSelectedNumericMetric,
            });
        }

        // Add export action if the handler support export data
        if (supportedExportDataFormats && supportedExportDataFormats.length > 0 && props.createExportFileContent
            && (selectedMetricsInfoWithData.numericMetrics.length > 0 || selectedMetricsInfoWithData.tagMetrics.length > 0)) {
            actions.push({
                label  : capitalizeAll(trans('label.export')),
                icon   : 'file_upload',
                onClick: onExportButtonClick,
            });
        }

        return actions;
    }, [onClearSelectedNumericMetric, onExportButtonClick, onShowViewOptionsButtonClick, props.createExportFileContent,
        props.highlightedMetrics?.numericMetrics, selectedMetricsInfoWithData.numericMetrics.length, selectedMetricsInfoWithData.tagMetrics.length,
        supportedExportDataFormats, trans]);



    // Find the longest metric name. We will use this value to set the width of the metric label
    const [metricLegendWidthInPixel, setMetricLegendWidthInPixel] = useState<number>(0);

    useLayoutEffect(() => {
        // Get all the labels displayed
        const metricsLabels = document.getElementsByClassName(classes.legendLabel);
        if (metricsLabels.length > 0) {
            // Use one of the labels and get the font settings for that label. To calculate the width of the metric correctly, we will
            // need to get the font settings
            const metricLabel = metricsLabels[0];
            const style = window.getComputedStyle(metricLabel);
            const fontStyle = `${style['font-weight']} ${style['font-size']} ${style['font-family']}`;

            // Get the longest metrics' name in pixel based on the legend's font style and then add 20 for the width of the bar
            // next to the legend and 10 for +- padding
            const longestMetricNameInPixel = getLongestMetricNameInPixel(
                selectedMetricsInfoWithData.numericMetrics, selectedMetricsInfoWithData.tagMetrics,
                props.metricViewSettings?.numericMetrics?.showLegends, props.metricViewSettings?.tagMetrics?.showLegends,
                fontStyle,
            ) + 30;

            // Make sure the width is at least 100 and less than 250 pixels
            setMetricLegendWidthInPixel(Math.max(100, Math.min(2500, longestMetricNameInPixel)));
        }
    }, [classes.legendLabel, props.metricViewSettings?.numericMetrics?.showLegends, props.metricViewSettings?.tagMetrics?.showLegends,
        selectedMetricsInfoWithData.numericMetrics, selectedMetricsInfoWithData.tagMetrics]);



    /**
     * Get the list of numeric metrics that we need to create legends for
     */
    const numericMetricsWithLegends = useMemo(() => {
        if (props.metricViewSettings?.numericMetrics?.showLegends !== false) {
            return selectedMetricsInfoWithData.numericMetrics.slice(
                // Pick first to maxLegends or metrics' length if maxLegends is not defined
                0, props.metricViewSettings?.numericMetrics?.maxLegends ?? selectedMetricsInfoWithData.numericMetrics.length,
            );
        }
        return [];
    }, [props.metricViewSettings?.numericMetrics?.maxLegends, props.metricViewSettings?.numericMetrics?.showLegends,
        selectedMetricsInfoWithData.numericMetrics]);


    /**
     * Get the list of tag metrics that we need to create legends for
     */
    const tagMetricsWithLegends = useMemo(() => {
        if (props.metricViewSettings?.tagMetrics?.showLegends !== false) {
            return selectedMetricsInfoWithData.tagMetrics.slice(
                // Pick first to maxLegends or metrics' length if maxLegends is not defined
                0, props.metricViewSettings?.tagMetrics?.maxLegends ?? selectedMetricsInfoWithData.tagMetrics.length,
            );
        }
        return [];
    }, [props.metricViewSettings?.tagMetrics?.maxLegends, props.metricViewSettings?.tagMetrics?.showLegends, selectedMetricsInfoWithData.tagMetrics]);



    /**
     * Sort the data points based on the tableViewSettings' sort order. NOTE that we will only need to do this if we need to show the
     * table view because this data is only used by the table view.
     */
    const sortedMetricsDataPointsForTableComp = useMemo(() => {
        const metricsDataPoints = globalChartData?.metricsDataPoints;

        if (metricsDataPoints && props.showTableView) {

            // First, we need to go through all the "metricsDataPoints" and replace tag value with their displayValue from the tagMetric's
            // enumDefinition. However, we only need to do this if there is at least 1 tag metric selected. If there is no tag metric selected then
            // return the `metricsDataPoints` as is.
            const localizedMetricsData = selectedMetricsInfoWithData?.tagMetrics && selectedMetricsInfoWithData.tagMetrics.length > 0

                // There is at least 1 tag metrics selected, replace tag value with display value
                ? metricsDataPoints.map((dataPoint) => {

                    // Make a copy of the dataPoint.
                    const localizedDataPoint = { ...dataPoint };

                    // For each selected tag metric, check if it has "enumDefinition". If it does, find the enum definition that matches
                    // the tagValue and replace the tagValue with the enum definition value.
                    selectedMetricsInfoWithData.tagMetrics.forEach((tagMetricInfo) => {
                        const tagValue = localizedDataPoint[tagMetricInfo.id];

                        if (tagMetricInfo.enumDefinitions && typeof tagValue === 'string') {
                            const enumDef = tagMetricInfo.enumDefinitions.find((enumDef) => {
                                return enumDef.value === tagValue;
                            });
                            if (enumDef) {
                                localizedDataPoint[tagMetricInfo.id] = enumDef.name;
                            }
                        }
                    });

                    return localizedDataPoint;
                })

                // No tag metrics selected, use the same "metricsDataPoints" but make a copy of the data so that if we sort the data, it doesn't
                // sort the original data.
                : [...metricsDataPoints];


            // Now sort the "localizedMetricsData" by the tableViewSettings.sortOrder if it is DESC.
            if (props.tableViewSettings && props.tableViewSettings.sortOrder === SortOrder.DESC) {
                return localizedMetricsData.sort((row1, row2) => {
                    return row2.dataPointTimestamp - row1.dataPointTimestamp;
                });
            }


            // If there is no sortOrder configure, just return the data as is.
            return localizedMetricsData;
        }

        // There is no "metricsDataPoints" or we are not showing "showTableView" so return undefined
        return undefined;
    }, [globalChartData?.metricsDataPoints, props.showTableView, props.tableViewSettings, selectedMetricsInfoWithData.tagMetrics]);


    // We don't display all data but only part of the data based on the current page so need to filter out the data points we don't need to show
    // This is used by the Table because it has Pagination.
    const filteredMetricsDataPoints = useMemo(() => {
        if (sortedMetricsDataPointsForTableComp) {
            const startIndex = searchParams.itemsPerPage * searchParams.currentPage;
            const endIndex = startIndex + searchParams.itemsPerPage;
            const filteredData = sortedMetricsDataPointsForTableComp.slice(startIndex, endIndex);
            return filteredData;
        }
        return undefined;
    }, [sortedMetricsDataPointsForTableComp, searchParams.itemsPerPage, searchParams.currentPage]);



    /**
     * Create the settings for the TimeSeriesDataTable component
     */
    const tableViewSettings = useMemo((): TimeSeriesDataTableProps => {
        const totalDataPoints = sortedMetricsDataPointsForTableComp?.length || 0;
        const metricsInfo = [...(selectedMetricsInfoWithData?.numericMetrics ?? []), ...(selectedMetricsInfoWithData?.tagMetrics ?? [])];

        return {
            title                 : props.title,
            icon                  : props.icon ?? 'view_column',
            customSecondaryActions: compDropDownOptions,
            listData              : filteredMetricsDataPoints,

            // Dynamically add the columns to the table based on the list of selected metrics.
            fieldsSettings: {
                fields: {
                    date: {
                        dataItemProp: 'date',
                        label       : capitalizeAll(trans('label.date')),
                    },
                    ...metricsInfo.reduce((
                        result: {[key: string]: TimeSeriesTableCompField},
                        metricInfo,
                    ): {[key: string]: TimeSeriesTableCompField} => {
                        return {
                            ...result,
                            [metricInfo.id]: {
                                label        : metricInfo.displayName,
                                unit         : metricInfo.unit,
                                dataItemProp : metricInfo.id,
                                decimalPlaces: metricInfo.decimalPlaces,
                            },
                        };
                    }, {}),
                },
            },
            decimalPlaces : 3,
            dateTimeFormat: {
                year                  : 'numeric',
                month                 : 'short',
                day                   : '2-digit',
                hour                  : '2-digit',
                minute                : '2-digit',
                second                : '2-digit',
                fractionalSecondDigits: 3,
                hour12                : false,
            },
            labels: {
                emptyValues: '-',
            },
            paginationComp: (
                <PaginationBar {...{
                    itemsPerPageList: ITEMS_PER_PAGE_LIST,
                    itemsPerPage    : searchParams.itemsPerPage,
                    pagingMinPages  : PAGING_MIN_PAGES,
                    currentPage     : searchParams.currentPage,
                    totalItems      : totalDataPoints,
                    hasMoreItems    : totalDataPoints > ((searchParams.currentPage + 1) * searchParams.itemsPerPage),
                    texts           : {
                        itemsPerPage: capitalizeAll(trans('label.paging.items_per_page', {
                            item_name: trans('label.item', { count: 2 }),
                        })),
                        pageNTemplate   : capitalizeAll(trans('label.page_n')),
                        pageNofMTemplate: capitalizeAll(trans('label.page_n_of_m')),
                    },
                    onItemsPerPageChange,
                    onNextPage,
                    onPrevPage,
                    onPage,
                }}
                />
            ),
            noDataMessageComp: (
                <GenericMessageBox
                    title={trans('generic.no_data.title', {
                        term: capitalizeAll(trans('label.time_series_data', { count: 2 })),
                    })}
                />
            ),
            emptyDataMessageComp: (
                <GenericMessageBox
                    title={trans('generic.no_data.title', {
                        term: capitalizeAll(trans('label.time_series_data', { count: 2 })),
                    })}
                />
            ),
        };
    }, [compDropDownOptions, filteredMetricsDataPoints, sortedMetricsDataPointsForTableComp?.length, onItemsPerPageChange, onNextPage, onPage, onPrevPage,
        props.icon, props.title, searchParams.currentPage, searchParams.itemsPerPage, selectedMetricsInfoWithData?.numericMetrics,
        selectedMetricsInfoWithData?.tagMetrics, trans]);


    return (
        <>
            {globalChartData && props.showChartView !== false && (
                <Paper elevation={2} className={clsx(panelClasses.root, props.className)} style={props.customStyles?.container}>
                    <CompPanelHeader
                        headerClassName={clsx(panelClasses.panelHeader, classes.panelHeader)}
                        borderlessHeader
                        title={props.title}
                        icon={props.icon ?? 'show_chart'}
                        customSecondaryActions={compDropDownOptions}
                    />
                    <div className={clsx(panelClasses.panelContent, classes.panelContent)} style={props.customStyles?.contentContainer}>
                        {(numericMetricsWithLegends.length > 0 || tagMetricsWithLegends.length > 0) && (
                            <div className={classes.legendsContainer} style={props.customStyles?.legendsContainer}>
                                {numericMetricsWithLegends.map((metric) => {
                                    return (
                                        <div
                                            key={metric.id}
                                            className={clsx(classes.legendLabel, 'numeric')}
                                            style={{
                                                color: metric.color,
                                                width: `${metricLegendWidthInPixel}px`,
                                            }}
                                            onClick={onShowViewOptionsButtonClick}
                                        >
                                            <span className="label">{metric.displayName ?? metric.name ?? metric.id}</span>
                                        </div>
                                    );
                                })}
                                {tagMetricsWithLegends.map((metric) => {
                                    return (
                                        <div
                                            key={metric.id}
                                            className={clsx(classes.legendLabel, 'tag')}
                                            style={{
                                                color: metric.color,
                                                width: `${metricLegendWidthInPixel}px`,
                                            }}
                                            onClick={onShowViewOptionsButtonClick}
                                        >
                                            <span className="label">{metric.displayName ?? metric.name ?? metric.id}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {selectedMetricsInfoWithData.numericMetrics.length > 0 || selectedMetricsInfoWithData.tagMetrics.length > 0 ? (
                            <SynchronizedNumericTagMetricsDisplayContainer
                                className={classes.chart}
                                numericChartStyle={props.customStyles?.numericChartStyle}
                                tagChartStyle={props.customStyles?.tagChartStyle}
                                chartId={`${props.chartIdPrefix}-MultiDataSourcesViewContainer}`}
                                chartSettings={chartSettings}
                                chartViewSettings={props.chartViewSettings}
                                numericMetrics={selectedMetricsInfoWithData?.numericMetrics}
                                tagMetrics={selectedMetricsInfoWithData?.tagMetrics}
                                metricsData={globalChartData}
                                texts={texts}
                                dateRange={chartDateRange}
                                showVerticalLineAtTimestamp={props.showVerticalLineAtTimestamp}
                                highlightedMetrics={props.highlightedMetrics}
                                onNumericMetricSeriesClicked={onNumericMetricSeriesClickedHandler}
                            />
                        ) : (
                            <GenericMessageBox
                                className={classes.noDataMessage}
                                title={trans('generic.no_data.title', {
                                    term: capitalizeAll(trans('label.data', { count: 2 })),
                                })}
                            />
                        )}
                    </div>
                </Paper>
            )}

            {globalChartData && props.showTableView === true && (
                <TimeSeriesDataTable
                    className={clsx(panelClasses.root, classes.viewPanel)}
                    {...tableViewSettings}
                />
            )}

            {!globalChartData && (
                <CircularProgress />
            )}
        </>
    );
};
